import {
  accountDeletionApiV1InternalAccountDeletionsRequestIdGet,
  accountDeletionsApiV1InternalAccountDeletionsGet,
  accountsApiV1InternalAccountsGet,
  changeMemberAccessApiV1InternalMembersMemberIdAccessPost,
  createAccountsBatchApiV1InternalAccountsBatchPost,
  createStylistApiV1InternalStylistsPost,
  createUserApiV1InternalUsersPost,
  editMemberPersonalProfileApiV1InternalUsersIdProfilePersonalPut,
  editMemberProfileMeasurementsApiV1InternalUsersIdProfileMeasurementsPut,
  editMemberProfilePresentationApiV1InternalUsersIdProfilePresentationPut,
  editMemberProfilePrivacyApiV1InternalUsersIdProfilePrivacyPut,
  getStylistApiV1InternalStylistsIdGet,
  getUserApiV1InternalUsersIdGet,
  listMembersApiV1InternalMembersGet,
  listStylistsApiV1InternalStylistsGet,
  manageAccountApiV1InternalAccountsIdPatch,
  memberDataExportStatusApiV1InternalUsersIdDataExportsExportIdGet,
  issuePasswordResetLinkApiV1InternalAccountsIdPasswordResetLinkPost,
  memberUsageLimitsApiV1InternalUsersIdUsageLimitsGet,
  regenerateMemberDailyLookApiV1InternalUsersIdDailyLookRegeneratePost,
  nudgeMemberOnboardingApiV1InternalUsersIdOnboardingNudgePost,
  requestMemberAccountDeletionApiV1InternalUsersIdDeletionRequestPost,
  requestMemberDataExportApiV1InternalUsersIdDataExportPost,
  downloadMemberDataExportApiV1InternalUsersIdDataExportsExportIdDownloadGet,
  retryAccountDeletionApiV1InternalAccountDeletionsRequestIdRetryPost,
  saveMemberOnboardingCheckpointApiV1InternalUsersIdOnboardingCheckpointPut,
  setMemberProfileLocationApiV1InternalUsersIdProfileLocationPut,
  setMemberUsageLimitsApiV1InternalUsersIdUsageLimitsPut,
  stylistOversightApiV1InternalStylistsIdOversightGet,
  readMemberNotificationPreferencesApiV1InternalUsersIdNotificationPreferencesGet,
  updateMemberNotificationPreferencesApiV1InternalUsersIdNotificationPreferencesPut,
  updateStylistApiV1InternalStylistsIdPatch,
  updateUserApiV1InternalUsersIdPatch,
  type AdminAccountCreateResult,
  type AdminAccountPage,
  type AdminMemberUsageLimits,
  type AdminOnBehalfLifecycleAccepted,
  type AdminOnBehalfJobAccepted,
  type AdminOnBehalfNudgeResult,
  type AdminOnBehalfOnboardingResult,
  type AdminOnBehalfActionResult,
  type AdminOnBehalfProfileResult,
  type AdminPasswordResetLink,
  type AdminStylistOversightPage,
  type ConsumerDetail,
  type ConsumerPage,
  type ConsumerSummary,
  type ListMembersApiV1InternalMembersGetData,
  type OperatorDeletionResponse,
  type PrivacyExportDownload,
  type PrivacyExportResponse,
  type StylistDetail,
  type StylistPage,
  type StylistSummary,
} from '@closet-theory/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText, Section, StatRow, StatTile } from '@ui/console/primitives';
import { Bar, IdentifierValue, Quantity, formatCount, plural } from '@ui/console/measures';
import { MetricBand } from '@ui/console/MetricBand';
import type { BandFigure } from '@ui/console/MetricBand';
import type { Tone } from '@ui/console/status';
import { Term } from '@ui/console/glossary';
import { formatRelative, formatTimestamp, humanize } from '@ui/console/format';
import { RecordTable } from './record-table';
import { CopyableId, EntityRef, IdentifierCopy, looksLikeUuid } from './references';
import { ActionDialog } from '@ui/console/PageShell';
import { CheckboxField, FormCard, SelectField, TextAreaField, TextField } from '@ui/console/forms';
import { configureAdminApi, currentRole, handleUnauthorized } from './auth';
import { canAccess } from './auth-policy';
import { errorDetail } from './operation-policy';
import { Actions, DetailList, Page, Pager, State, Status } from './components';
import { ConsumerSchedulingDialog } from './consumer-scheduling';
import { MemberInsights, resolveInsightTab, useLiveRefresh } from './consumer-insights';
import { MemberDeletionBanner } from './account-restore';
import type { InsightTabId } from './consumer-insights';
import { parseLimit, WRITABLE_LIMITS } from './usage-limit-fields';
import {
  ForbiddenRead,
  readActiveStylists,
  readAllMembers,
  StylistMembers,
} from './stylist-assignments';

const LIMIT = 25;

/** What a figure has to say for itself before the list has arrived. */
const NOT_YET = 'The list has not come back yet.';

/**
 * The figure a list screen's band leads with: how many rows the filters match
 * in the table, which the server counts rather than this page.
 */
function matchingFigure(total: number | undefined, singular: string, subject: string): BandFigure {
  return {
    id: 'matching',
    label: `${subject} matching these filters`,
    ...(total === undefined
      ? { absent: NOT_YET }
      : { value: formatCount(total), unit: plural(total, singular), raw: total }),
    hint: 'Counted by the server over the whole table, not over the page on screen.',
  };
}

/**
 * One of the rows these filters match, drawn as a share of all of them.
 *
 * Both numbers come from the same query the list is built from, so the figure
 * and its bar have one denominator and it is the filtered table. Counted here
 * instead, over the page in hand, the figure would sit beside a server total
 * looking like the same kind of number while answering a narrower question,
 * and its bar would read as a share of the whole table it was never counted
 * over. The stylist band made that worse still by dividing two page-local sums
 * and calling the result roster utilisation.
 */
function shareOfMatching(figure: {
  id: string;
  label: string;
  /** How many of the matching rows this is, from the server's own split. */
  count: number | undefined;
  /** How many rows match at all, the denominator for both figure and bar. */
  total: number | undefined;
  /** What one of them is: `account`, `member`. */
  noun: string;
  /** Its plural, when adding an `s` is wrong. */
  nouns?: string;
  /** The bar's sentence, without its numbers: they are appended. */
  reading: string;
  hint: string;
  tone?: Tone;
}): BandFigure {
  const { count, total } = figure;
  if (count === undefined || total === undefined) {
    return { id: figure.id, label: figure.label, absent: NOT_YET, hint: figure.hint };
  }
  return {
    id: figure.id,
    label: figure.label,
    value: formatCount(count),
    unit: plural(count, figure.noun, figure.nouns),
    raw: count,
    bar: {
      fraction: total ? count / total : 0,
      label: `${figure.reading}: ${count} of ${total} matching these filters`,
      ...(figure.tone ? { tone: figure.tone } : {}),
    },
    hint: figure.hint,
  };
}

type AccountDraft = {
  row_id: string;
  name: string;
  email: string;
  role: 'consumer' | 'stylist' | 'manager' | 'admin' | 'super_admin';
  capacity_per_day: string;
};

const blankAccount = (): AccountDraft => ({
  row_id: crypto.randomUUID(),
  name: '',
  email: '',
  role: 'consumer',
  capacity_per_day: '',
});

const accountError = (row: AccountDraft) => {
  if (!row.name.trim()) return 'Name is required';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(row.email.trim())) return 'Valid email is required';
  if (row.role === 'stylist' && !(Number(row.capacity_per_day) > 0)) return 'Capacity is required';
  return undefined;
};

// The retry route accepts a request the processor has given up on
// (`retryable_failure` or `manual_review`), at most three operator retries in
// all (app/members/account_deletion/repository.py).
const RETRYABLE_DELETION_STATUSES = new Set(['retryable_failure', 'manual_review']);
const MAX_OPERATOR_DELETION_RETRIES = 3;

function deletionIsRetryable(request: OperatorDeletionResponse): boolean {
  return (
    RETRYABLE_DELETION_STATUSES.has(request.status) &&
    (request.operator_retry_count ?? 0) < MAX_OPERATOR_DELETION_RETRIES
  );
}

/**
 * The processing queue's status filter. The route takes one status at a time,
 * so the default, the two statuses that need a person, is read as two requests
 * and merged.
 */
const DELETION_QUEUE_FILTERS = [
  { value: 'attention', label: 'Needs attention (failed or manual review)' },
  { value: 'retryable_failure', label: 'Failed, can be retried' },
  { value: 'manual_review', label: 'Needs manual review' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'quiescing', label: 'Quiescing' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: '', label: 'All statuses' },
];
const DELETION_QUEUE_PAGE = 25;

/** `total` is the server's count over every matching request, summed across the reads. */
type DeletionQueue = { items: OperatorDeletionResponse[]; total: number };

async function readDeletionQueue(
  filter: string,
): Promise<{ status: number; queue?: DeletionQueue; error?: unknown } | undefined> {
  const statuses: Array<string | undefined> =
    filter === 'attention' ? [...RETRYABLE_DELETION_STATUSES] : [filter || undefined];
  const results = await Promise.all(
    statuses.map((status) =>
      accountDeletionsApiV1InternalAccountDeletionsGet({
        query: { limit: DELETION_QUEUE_PAGE, offset: 0, ...(status ? { status } : {}) },
      }),
    ),
  ).catch(() => undefined);
  if (!results) return undefined;
  for (const result of results) handleUnauthorized(result.response.status);
  const failed =
    results.find((result) => result.response.status === 403) ??
    results.find((result) => result.error || !result.data);
  if (failed) return { status: failed.response.status, error: failed.error };
  // A request that changed status between the two reads would otherwise be listed twice.
  const items = [
    ...new Map(
      results.flatMap((result) => result.data?.items ?? []).map((item) => [item.id, item]),
    ).values(),
  ].sort((left, right) => right.updated_at.localeCompare(left.updated_at));
  return {
    status: 200,
    queue: {
      items: items.slice(0, DELETION_QUEUE_PAGE),
      // The statuses read are disjoint, so their totals add up to the whole set.
      total: results.reduce((sum, result) => sum + (result.data?.total ?? 0), 0),
    },
  };
}

export function AccountDeletionProcessingQueue() {
  const [filter, setFilter] = useState('attention');
  const [queue, setQueue] = useState<DeletionQueue>();
  const [selected, setSelected] = useState<OperatorDeletionResponse>();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [feedback, setFeedback] = useState<
    | { kind: 'forbidden'; capability: string }
    | { kind: 'error'; title: string; detail: string }
    | { kind: 'success'; title: string }
  >();

  const loadQueue = useCallback(
    async (preserve = false) => {
      if (!preserve) {
        setLoading(true);
        setFeedback(undefined);
      }
      configureAdminApi();
      const result = await readDeletionQueue(filter);
      if (!result) {
        if (!preserve) {
          setLoading(false);
          setFeedback({
            kind: 'error',
            title: 'The account-deletion processing queue was not loaded.',
            detail: 'The account-deletion processing queue could not be reached.',
          });
        }
        return;
      }
      if (!preserve) setLoading(false);
      if (result.status === 403) {
        if (!preserve)
          setFeedback({ kind: 'forbidden', capability: 'list account-deletion processing jobs' });
      } else if (!result.queue) {
        if (!preserve)
          setFeedback({
            kind: 'error',
            title: 'The account-deletion processing queue was not loaded.',
            detail: errorDetail(
              result.error,
              'The account-deletion processing queue could not be loaded.',
            ),
          });
      } else {
        setQueue(result.queue);
      }
    },
    [filter],
  );

  useEffect(() => void loadQueue(), [loadQueue]);
  useLiveRefresh(() => loadQueue(true), Boolean(selected));

  const loadDetail = async (requestId: string) => {
    setLoading(true);
    setFeedback(undefined);
    configureAdminApi();
    const result = await accountDeletionApiV1InternalAccountDeletionsRequestIdGet({
      path: { request_id: requestId },
    });
    handleUnauthorized(result.response.status);
    setLoading(false);
    if (result.response.status === 403) {
      setFeedback({ kind: 'forbidden', capability: 'view account-deletion processing detail' });
    } else if (result.error || !result.data) {
      setFeedback({
        kind: 'error',
        title: 'The account-deletion processing detail was not loaded.',
        detail: errorDetail(
          result.error,
          'The account-deletion processing detail could not be loaded.',
        ),
      });
    } else {
      setSelected(result.data);
    }
  };

  const retry = async () => {
    if (!selected) return;
    setBusy(true);
    setFeedback(undefined);
    configureAdminApi();
    const result = await retryAccountDeletionApiV1InternalAccountDeletionsRequestIdRetryPost({
      path: { request_id: selected.id },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    setConfirming(false);
    if (result.response.status === 403) {
      setFeedback({
        kind: 'forbidden',
        capability: 'retry failed account-deletion processing jobs',
      });
    } else if (result.error || !result.data) {
      setFeedback({
        kind: 'error',
        title: 'The account-deletion retry was not requested.',
        detail: errorDetail(result.error, 'The account-deletion retry could not be requested.'),
      });
    } else {
      setSelected(result.data);
      setQueue((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) => (item.id === result.data.id ? result.data : item)),
            }
          : current,
      );
      setFeedback({ kind: 'success', title: 'Account-deletion retry requested.' });
    }
  };

  return (
    <Section
      title="Account-deletion processing queue"
      description="This is the irreversible deletion processor and its retry history. It is not the separate Deletion requests screen, which tracks restorable requests and offers restore. Nothing in this queue can be restored here."
      actions={
        <Actions>
          <SelectField
            label="Processing status"
            value={filter}
            onChange={(event) => setFilter(event.currentTarget.value)}
            options={DELETION_QUEUE_FILTERS}
          />
          <Button size="sm" color="secondary" isDisabled={loading} onClick={() => void loadQueue()}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Actions>
      }
    >
      {feedback?.kind === 'forbidden' ? (
        <Callout tone="warning" title={`Your role cannot ${feedback.capability}.`}>
          That account-deletion capability is limited to Admin and Super Admin. Nothing was changed
          and you are still signed in.
        </Callout>
      ) : feedback?.kind === 'error' ? (
        <Callout tone="error" title={feedback.title}>
          {feedback.detail}
        </Callout>
      ) : feedback?.kind === 'success' ? (
        <Callout tone="success" title={feedback.title} />
      ) : null}
      {loading && !queue ? <span className="text-sm text-tertiary">Loading…</span> : null}
      {queue && queue.items.length === 0 ? (
        <Callout
          tone="neutral"
          title={
            filter === 'attention'
              ? 'No account deletion has failed or is waiting for manual review.'
              : 'No account-deletion processing requests with this status.'
          }
        />
      ) : queue ? (
        <>
          <RecordTable
            noun="account-deletion processing requests"
            searchable={false}
            records={queue.items}
            columns={[
              'id',
              'member_id',
              'status',
              'stage',
              'attempt',
              'max_attempts',
              'error_code',
              'updated_at',
            ]}
            rowActions={(record) => (
              <Button
                size="sm"
                color="secondary"
                onClick={() => void loadDetail(String(record.id))}
              >
                View processing detail
              </Button>
            )}
          />
          {queue.total > queue.items.length ? (
            <span className="text-xs text-tertiary">
              Showing {formatCount(queue.items.length)} of {formatCount(queue.total)}{' '}
              {plural(queue.total, 'request')}, most recently updated first.
            </span>
          ) : null}
        </>
      ) : null}
      {selected ? (
        <ActionDialog
          title="Account-deletion processing detail"
          description="The processor’s current record, retained exceptions, and the outcome reported by each processor."
          width="lg"
          onClose={() => {
            setSelected(undefined);
            setConfirming(false);
          }}
          footer={
            <Button
              size="md"
              color="secondary"
              isDisabled={busy}
              onClick={() => setSelected(undefined)}
            >
              Close
            </Button>
          }
        >
          <Section title="Selected processing request">
            <DetailList
              items={[
                ['Deletion request', <IdentifierCopy label="Request" value={selected.id} />],
                ...(selected.member_id
                  ? ([
                      [
                        'Member',
                        <EntityRef
                          kind="member"
                          id={selected.member_id}
                          label={selected.member_name}
                        />,
                      ],
                    ] as Array<[ReactNode, ReactNode]>)
                  : []),
                ['Status', <Status value={selected.status} />],
                ['Stage', selected.stage],
                ['Attempt', `${selected.attempt} of ${selected.max_attempts}`],
                ['Operator retries', formatCount(selected.operator_retry_count ?? 0)],
                ['Objects deleted', formatCount(selected.object_delete_count)],
                ...(selected.error_code ||
                ['retryable_failure', 'manual_review'].includes(selected.status)
                  ? ([['Error code', selected.error_code ?? 'None reported']] as Array<
                      [ReactNode, ReactNode]
                    >)
                  : []),
                ['Requested', new Date(selected.requested_at).toLocaleString()],
                ['Effective', new Date(selected.effective_at).toLocaleString()],
                ['Updated', new Date(selected.updated_at).toLocaleString()],
                ...(selected.completed_at || selected.status === 'completed'
                  ? ([
                      [
                        'Completed',
                        selected.completed_at
                          ? new Date(selected.completed_at).toLocaleString()
                          : 'Not recorded',
                      ],
                    ] as Array<[ReactNode, ReactNode]>)
                  : []),
                [
                  'Retained exceptions',
                  selected.retained_exceptions.length
                    ? selected.retained_exceptions.join(', ')
                    : 'None reported',
                ],
              ]}
            />
            {Object.keys(selected.processor_outcomes).length ? (
              <Section title="Processor outcomes">
                <DetailList
                  items={Object.entries(selected.processor_outcomes).map(([processor, outcome]) => [
                    humanize(processor),
                    String(outcome),
                  ])}
                />
              </Section>
            ) : (
              <Callout tone="neutral" title="No processor outcomes reported." />
            )}
            {confirming ? (
              <ConfirmStep
                title="Request another deletion attempt?"
                body="This asks the deletion processor to retry this failed request. It does not confirm that deletion completes."
                busy={busy}
                onCancel={() => setConfirming(false)}
                onConfirm={() => void retry()}
              />
            ) : (
              <Actions>
                <Button
                  size="md"
                  color="primary-destructive"
                  isDisabled={busy || !deletionIsRetryable(selected)}
                  onClick={() => setConfirming(true)}
                >
                  Retry failed deletion
                </Button>
              </Actions>
            )}
          </Section>
        </ActionDialog>
      ) : null}
    </Section>
  );
}

export function Accounts() {
  const [params, setParams] = useSearchParams();
  const query = params.get('query') ?? '';
  const role = params.get('role') ?? '';
  const status = params.get('status') ?? '';
  const offset = Number(params.get('offset') ?? 0);
  const [rows, setRows] = useState<AccountDraft[]>([blankAccount()]);
  const [results, setResults] = useState<AdminAccountCreateResult[]>([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionId, setActionId] = useState<string>();
  const [error, setError] = useState<string>();
  const [pendingBatch, setPendingBatch] = useState<{ key: string; signature: string }>();
  const operatorRole = currentRole();
  const availableRoles: AccountDraft['role'][] =
    operatorRole === 'super_admin'
      ? ['consumer', 'stylist', 'manager', 'admin', 'super_admin']
      : operatorRole === 'admin'
        ? ['consumer', 'stylist', 'manager']
        : ['consumer', 'stylist'];
  const filter = (next: { query?: string; role?: string; status?: string; offset?: number }) =>
    setParams({
      ...((next.query ?? query) ? { query: next.query ?? query } : {}),
      ...((next.role ?? role) ? { role: next.role ?? role } : {}),
      ...((next.status ?? status) ? { status: next.status ?? status } : {}),
      ...(next.offset ? { offset: String(next.offset) } : {}),
    });
  const source = useLoad<AdminAccountPage>(async () => {
    const result = await accountsApiV1InternalAccountsGet({
      query: {
        limit: LIMIT,
        offset,
        ...(query ? { query } : {}),
        ...(role
          ? {
              role: role as 'consumer' | 'stylist' | 'manager' | 'admin' | 'super_admin',
            }
          : {}),
        ...(status ? { status: status as 'holding' | 'invited' | 'active' | 'disabled' } : {}),
      },
    });
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) throw new Error('Account roster could not be loaded.');
    return result.data;
  }, [query, role, status, offset]);
  const updateRow = (id: string, patch: Partial<AccountDraft>) =>
    setRows((current) => current.map((row) => (row.row_id === id ? { ...row, ...patch } : row)));
  const create = async () => {
    const invalid = rows.some(accountError);
    if (invalid) {
      setError('Correct the highlighted rows before creating accounts.');
      return;
    }
    setBusy(true);
    setError(undefined);
    configureAdminApi();
    const accounts = rows.map((row) => ({
      row_id: row.row_id,
      name: row.name.trim(),
      email: row.email.trim(),
      role: row.role,
      capacity_per_day: row.role === 'stylist' ? Number(row.capacity_per_day) : null,
    }));
    const signature = JSON.stringify(accounts);
    const attempt =
      pendingBatch?.signature === signature
        ? pendingBatch
        : { key: crypto.randomUUID(), signature };
    setPendingBatch(attempt);
    const result = await createAccountsBatchApiV1InternalAccountsBatchPost({
      headers: { 'Idempotency-Key': attempt.key },
      body: {
        reason: 'Create pilot accounts from Admin',
        accounts,
      },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.error || !result.data) {
      setError('The account batch could not be submitted.');
      return;
    }
    setPendingBatch(undefined);
    setResults(result.data.results);
    const remaining = rows.filter(
      (row) => result.data?.results.find((item) => item.row_id === row.row_id)?.status === 'failed',
    );
    setRows(remaining.length ? remaining : [blankAccount()]);
    // Nothing left to correct means the dialog has done its job; a row that
    // failed keeps it open, with that row still in it.
    if (!remaining.length) setCreating(false);
    await source.refresh();
  };
  const manage = async (
    id: string,
    command: { status?: 'active' | 'disabled'; resend?: 'invitation' | 'reset' },
  ) => {
    setActionId(id);
    configureAdminApi();
    const result = await manageAccountApiV1InternalAccountsIdPatch({
      path: { id },
      body: { ...command, reason: 'Manage pilot account from Admin' },
    });
    handleUnauthorized(result.response.status);
    setActionId(undefined);
    if (result.error) setError('The account action was rejected.');
    else await source.refresh();
  };
  const accounts = source.data?.breakdown;
  return (
    <Page
      title="Accounts"
      purpose="Use this page to create staff and member accounts, and to enable, disable or re-invite an existing one. You can only create roles at or below your own."
      band={
        <MetricBand
          label="The accounts these filters match"
          figures={[
            matchingFigure(source.data?.total, 'account', 'Accounts'),
            shareOfMatching({
              id: 'active',
              label: 'Able to sign in',
              count: accounts?.active,
              total: source.data?.total,
              noun: 'account',
              reading: 'Accounts able to sign in',
              tone: 'success',
              hint: 'Enabled, with a credential. A disabled account keeps its record and its history.',
            }),
            shareOfMatching({
              id: 'invited',
              label: 'Invited, not yet accepted',
              count: accounts?.invited,
              total: source.data?.total,
              noun: 'account',
              reading: 'Accounts still on an unaccepted invitation',
              tone: 'warning',
              hint: 'They have been sent an invitation and have not signed in with it yet. Re-invite from the row.',
            }),
          ]}
        />
      }
      actions={
        <Button size="md" color="primary" onClick={() => setCreating(true)}>
          Create accounts
        </Button>
      }
      toolbar={{
        search: {
          initial: query,
          placeholder: 'Name or email',
          onSearch: (value) => filter({ query: value, offset: 0 }),
        },
        onRefresh: () => void source.refresh(),
        filters: (
          <>
            <SelectField
              label="Role"
              value={role}
              onChange={(event) => filter({ role: event.currentTarget.value, offset: 0 })}
              options={[
                { value: '', label: 'All roles' },
                ...availableRoles.map((item) => ({ value: item, label: humanize(item) })),
              ]}
            />
            <SelectField
              label="Status"
              value={status}
              onChange={(event) => filter({ status: event.currentTarget.value, offset: 0 })}
              options={[
                { value: '', label: 'All statuses' },
                { value: 'holding', label: 'Holding (waiting for access)' },
                { value: 'invited', label: 'Invited' },
                { value: 'active', label: 'Active' },
                { value: 'disabled', label: 'Disabled' },
              ]}
            />
          </>
        ),
      }}
    >
      {/* The dialog shows its own failure; the page shows the ones from row actions. */}
      {error && !creating ? <Callout tone="error" title={error} /> : null}
      {results.length ? (
        <Section
          title="Last account batch"
          description="Each row was validated and reported independently."
        >
          <ul aria-label="Account creation results" className="flex flex-col gap-2">
            {results.map((result) => (
              <li key={result.row_id} className="flex items-center gap-2 text-sm text-secondary">
                <Status value={result.status} /> {result.account?.email ?? result.error_code}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {canAccess(operatorRole, 'admin:privileged-operator') ? (
        <AccountDeletionProcessingQueue />
      ) : null}
      <State {...source} empty={!source.data?.items.length} onRetry={() => void source.refresh()}>
        <RecordTable
          noun="accounts"
          searchable={false}
          records={source.data?.items ?? []}
          columns={['name', 'email', 'role', 'status', 'profile_status', 'created_at']}
          columnLabel={(column) =>
            column === 'name'
              ? 'Account'
              : column === 'profile_status'
                ? 'Profile status'
                : column === 'created_at'
                  ? 'Created'
                  : undefined
          }
          renderCell={(column, record) =>
            column === 'name' && typeof record.id === 'string' ? (
              // Members and stylists have their own pages; staff accounts do
              // not, so they read by name without a link.
              <EntityRef
                kind={
                  record.role === 'stylist'
                    ? 'stylist'
                    : record.role === 'consumer'
                      ? 'member'
                      : 'staff'
                }
                id={record.id}
                label={typeof record.name === 'string' ? record.name : undefined}
              />
            ) : column === 'profile_status' && typeof record.profile_status === 'string' ? (
              <Status value={record.profile_status} />
            ) : undefined
          }
          rowActions={(record) => {
            const item = record as unknown as AdminAccountPage['items'][number];
            return (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  color="secondary"
                  isDisabled={actionId === item.id}
                  onClick={() =>
                    void manage(item.id, {
                      status: item.status === 'disabled' ? 'active' : 'disabled',
                    })
                  }
                >
                  {item.status === 'disabled' ? 'Enable' : 'Disable'}
                </Button>
                <Button
                  size="sm"
                  color="secondary"
                  isDisabled={actionId === item.id}
                  onClick={() =>
                    void manage(item.id, {
                      resend: item.status === 'invited' ? 'invitation' : 'reset',
                    })
                  }
                >
                  {item.status === 'invited' ? 'Resend invite' : 'Send reset'}
                </Button>
              </div>
            );
          }}
        />
        {source.data ? (
          <Pager {...source.data} onPage={(next) => filter({ offset: next })} />
        ) : null}
      </State>
      {creating ? (
        <ActionDialog
          title="Create accounts"
          description="Add as many rows as you need and submit them together. Each row succeeds or fails on its own, so one bad email does not lose the rest. Creating an account sends its invitation email straight away; assigning a stylist is done separately. Nothing is created until you press Create accounts."
          width="lg"
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button
                size="md"
                color="secondary"
                isDisabled={busy}
                onClick={() => setCreating(false)}
              >
                Cancel
              </Button>
              <Button
                size="md"
                color="secondary"
                onClick={() => setRows((current) => [...current, blankAccount()])}
              >
                Add another
              </Button>
              <Button size="md" color="primary" isDisabled={busy} onClick={() => void create()}>
                {busy
                  ? 'Creating…'
                  : `Create ${rows.length} account${rows.length === 1 ? '' : 's'}`}
              </Button>
            </>
          }
        >
          {error ? <Callout tone="error" title={error} /> : null}
          {rows.map((row, index) => {
            const validation = accountError(row);
            return (
              <fieldset
                key={row.row_id}
                className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-5"
              >
                <legend className="px-1 text-sm font-semibold text-primary">
                  Account {index + 1}
                </legend>
                <TextField
                  label="Full name"
                  value={row.name}
                  onChange={(event) => updateRow(row.row_id, { name: event.currentTarget.value })}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={row.email}
                  onChange={(event) => updateRow(row.row_id, { email: event.currentTarget.value })}
                />
                <SelectField
                  label="Role"
                  value={row.role}
                  onChange={(event) =>
                    updateRow(row.row_id, {
                      role: event.currentTarget.value as AccountDraft['role'],
                    })
                  }
                  options={availableRoles.map((item) => ({
                    value: item,
                    label: humanize(item),
                  }))}
                />
                {row.role === 'stylist' ? (
                  <TextField
                    label="Daily capacity"
                    hint="How many members this stylist can be assigned in one day, from 1 to 100."
                    type="number"
                    min="1"
                    max="100"
                    value={row.capacity_per_day}
                    onChange={(event) =>
                      updateRow(row.row_id, { capacity_per_day: event.currentTarget.value })
                    }
                  />
                ) : null}
                {validation ? (
                  <span className="text-xs text-error-primary">{validation}</span>
                ) : null}
                {rows.length > 1 ? (
                  <Actions>
                    <Button
                      size="sm"
                      color="secondary"
                      onClick={() =>
                        setRows((current) => current.filter((item) => item.row_id !== row.row_id))
                      }
                    >
                      Remove
                    </Button>
                  </Actions>
                ) : null}
              </fieldset>
            );
          })}
        </ActionDialog>
      ) : null}
    </Page>
  );
}

function useLoad<T>(load: () => Promise<T>, dependencies: readonly unknown[]) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async (options?: { preserve?: boolean }) => {
    if (!options?.preserve) {
      setLoading(true);
      setError(undefined);
    }
    try {
      configureAdminApi();
      setData(await load());
    } catch (cause) {
      if (!options?.preserve)
        setError(cause instanceof Error ? cause.message : 'This data could not be loaded.');
    } finally {
      if (!options?.preserve) setLoading(false);
    }
  }, dependencies);
  useEffect(() => void refresh(), [refresh]);
  useLiveRefresh(() => refresh({ preserve: true }));
  return { data, error, loading, refresh, setData };
}

/**
 * Several reads on these pages are gated on a permission the operator may not
 * hold — the stylist calendar wants scheduling rights, the oversight read wants
 * stylist rights. A 403 on a read is a boundary, not a broken page, so the
 * loader returns it as a value rather than throwing it as a failure.
 */
const FORBIDDEN = 'forbidden';
type OrForbidden<T> = T | typeof FORBIDDEN;

function PermissionBoundary({ subject }: { subject: string }) {
  return (
    <Callout tone="warning" title={`Your role cannot see ${subject}.`}>
      The read was refused because of your role, not because anything is broken, and you are still
      signed in. Ask an admin to widen your access.
    </Callout>
  );
}

type AccessStatus = ConsumerSummary['access_status'];

type AccessChange = {
  members: ConsumerSummary[];
  status: AccessStatus;
};

type AccessReport = {
  succeeded: Array<{ id: string; name: string }>;
  failed: Array<{ id: string; name: string; detail: string }>;
};

const SIGNUP_SOURCE: Record<ConsumerSummary['signup_source'], string> = {
  password: 'Email & password',
  google: 'Google',
  apple: 'Apple',
  operator: 'Added by staff',
};

function accessRows(items: ConsumerSummary[]) {
  return items.map((item) => {
    const row = {
      name: item.name,
      email: item.email,
      how_they_signed_up: SIGNUP_SOURCE[item.signup_source] ?? humanize(item.signup_source),
      joined_at: item.created_at,
    };
    // RecordTable uses `id` for identity and selection, but a hidden property
    // keeps the operator-facing columns limited to the four fields this queue needs.
    Object.defineProperty(row, 'id', { value: item.id, enumerable: false });
    return row as typeof row & { id: string };
  });
}

function AccessChangeDialog({
  change,
  onClose,
  onComplete,
}: {
  change: AccessChange;
  onClose: () => void;
  onComplete: (report: AccessReport) => Promise<void>;
}) {
  const approving = change.status === 'approved';
  const [reason, setReason] = useState(
    approving ? 'Approved from waitlist' : 'Returned to waitlist after access review',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const valid = reason.trim().length >= 3;
  const bulk = change.members.length > 1;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(undefined);
    configureAdminApi();
    const report: AccessReport = { succeeded: [], failed: [] };
    for (const member of change.members) {
      try {
        const result = await changeMemberAccessApiV1InternalMembersMemberIdAccessPost({
          path: { member_id: member.id },
          body: {
            status: change.status,
            reason: reason.trim(),
            idempotency_key: crypto.randomUUID(),
          },
        });
        handleUnauthorized(result.response.status);
        if (result.error || !result.data) {
          report.failed.push({
            id: member.id,
            name: member.name,
            detail: errorDetail(result.error, 'The access change was rejected.'),
          });
        } else {
          report.succeeded.push({ id: member.id, name: member.name });
        }
      } catch (cause) {
        report.failed.push({
          id: member.id,
          name: member.name,
          detail: errorDetail(cause, 'The access change could not be reached.'),
        });
      }
    }
    setBusy(false);
    if (!bulk && report.failed[0]) {
      setError(report.failed[0].detail);
      return;
    }
    await onComplete(report);
    onClose();
  };

  return (
    <ActionDialog
      title={
        bulk
          ? `${approving ? 'Approve access for' : 'Move'} ${change.members.length} members${approving ? '' : ' back to the waitlist'}`
          : `${approving ? 'Approve access for' : 'Move back to waitlist:'} ${change.members[0]?.name ?? 'member'}`
      }
      description={
        approving
          ? 'They will be able to use the consumer app after this change.'
          : 'They will be blocked from the consumer app until access is approved again.'
      }
      onClose={onClose}
      footer={
        <Button
          size="md"
          color={approving ? 'primary' : 'primary-destructive'}
          isDisabled={busy || !valid}
          onClick={() => void submit()}
        >
          {busy
            ? 'Saving…'
            : bulk
              ? `${approving ? 'Approve' : 'Move'} ${change.members.length} members`
              : approving
                ? 'Approve access'
                : 'Move back to waitlist'}
        </Button>
      }
    >
      <TextAreaField
        label="Reason"
        required
        minLength={3}
        maxLength={1000}
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
        hint="Required for the audit log; at least 3 characters."
      />
      {error ? <Callout tone="error" title={error} /> : null}
    </ActionDialog>
  );
}

export function Waitlist() {
  const [params, setParams] = useSearchParams();
  const query = params.get('query') ?? '';
  const accessStatus: AccessStatus = params.get('view') === 'approved' ? 'approved' : 'waitlisted';
  const offset = Number(params.get('offset') ?? 0);
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<AccessChange>();
  const [report, setReport] = useState<AccessReport>();
  const source = useLoad<ConsumerPage>(async () => {
    const result = await listMembersApiV1InternalMembersGet({
      query: {
        access_status: accessStatus,
        limit: LIMIT,
        offset,
        ...(query ? { query } : {}),
      },
    });
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) throw new Error('The access waitlist could not be loaded.');
    return result.data;
  }, [query, accessStatus, offset]);
  const navigate = (next: { query?: string; view?: AccessStatus; offset?: number }) => {
    setSelected([]);
    setReport(undefined);
    const nextView = next.view ?? accessStatus;
    setParams({
      ...((next.query ?? query) ? { query: next.query ?? query } : {}),
      ...(nextView === 'approved' ? { view: 'approved' } : {}),
      ...(next.offset ? { offset: String(next.offset) } : {}),
    });
  };
  const members = source.data?.items ?? [];
  const rows = accessRows(members);
  const selectedMembers = members.filter((member) => selected.includes(member.id));
  const complete = async (nextReport: AccessReport) => {
    setReport(nextReport);
    setSelected([]);
    if (nextReport.succeeded.length) {
      const changedIds = new Set(nextReport.succeeded.map((item) => item.id));
      source.setData((current) =>
        current
          ? {
              ...current,
              total: Math.max(0, current.total - nextReport.succeeded.length),
              items: current.items.filter((item) => !changedIds.has(item.id)),
            }
          : current,
      );
    }
    await source.refresh();
  };
  return (
    <Page
      title="Waitlist"
      purpose="New sign-ups wait here until someone approves their access to the app."
      band={
        <MetricBand
          label={accessStatus === 'waitlisted' ? 'Waiting for access' : 'Approved access'}
          figures={[
            matchingFigure(
              source.data?.total,
              'member',
              accessStatus === 'waitlisted' ? 'Waitlisted members' : 'Approved members',
            ),
            shareOfMatching({
              id: 'active',
              label: 'Finished onboarding',
              count: source.data?.breakdown.active,
              total: source.data?.total,
              noun: 'member',
              reading: 'Members past onboarding and not paused',
              tone: 'success',
              hint:
                accessStatus === 'waitlisted'
                  ? 'They have set up their profile and are ready to be let in.'
                  : 'Past onboarding and not paused. The rest are still onboarding or paused.',
            }),
          ]}
        />
      }
      toolbar={{
        search: {
          initial: query,
          placeholder: 'Name or email',
          onSearch: (value) => navigate({ query: value, offset: 0 }),
        },
        onRefresh: () => void source.refresh(),
        filters: (
          <SelectField
            label="View"
            value={accessStatus}
            onChange={(event) =>
              navigate({ view: event.currentTarget.value as AccessStatus, offset: 0 })
            }
            options={[
              { value: 'waitlisted', label: 'Waitlisted' },
              { value: 'approved', label: 'Approved' },
            ]}
          />
        ),
      }}
    >
      {report ? (
        <Callout
          tone={report.failed.length ? 'warning' : 'success'}
          title={`${report.succeeded.length} access ${report.succeeded.length === 1 ? 'change' : 'changes'} saved${report.failed.length ? `; ${report.failed.length} failed` : '.'}`}
          actions={
            <Button size="sm" color="secondary" onClick={() => setReport(undefined)}>
              Dismiss results
            </Button>
          }
        >
          {report.succeeded.length + report.failed.length > 1 ? (
            <ul className="flex flex-col gap-1" aria-label="Access change results">
              {report.succeeded.map((success) => (
                <li key={success.id}>
                  <strong>{success.name}:</strong> access changed
                </li>
              ))}
              {report.failed.map((failure) => (
                <li key={failure.id}>
                  <strong>{failure.name}:</strong> {failure.detail}
                </li>
              ))}
            </ul>
          ) : null}
        </Callout>
      ) : null}
      {selectedMembers.length ? (
        <Callout
          tone="neutral"
          title={`${selectedMembers.length} ${selectedMembers.length === 1 ? 'member' : 'members'} selected.`}
          actions={
            <Actions>
              <Button size="sm" color="secondary" onClick={() => setSelected([])}>
                Clear selection
              </Button>
              <Button
                size="sm"
                color="primary"
                onClick={() => setChange({ members: selectedMembers, status: 'approved' })}
              >
                Approve selected
              </Button>
            </Actions>
          }
        />
      ) : null}
      <State {...source} empty={!members.length} onRetry={() => void source.refresh()}>
        <RecordTable
          noun="members"
          searchable={false}
          records={rows}
          columns={['name', 'email', 'how_they_signed_up', 'joined_at']}
          {...(accessStatus === 'waitlisted'
            ? {
                selection: {
                  selected,
                  onChange: setSelected,
                  idFor: (record: Record<string, unknown>) => String(record.id),
                  itemNoun: 'member',
                },
              }
            : {})}
          rowActions={(record: Record<string, unknown>) => {
            const member = members.find((item) => item.id === record.id);
            return member ? (
              <Button
                size="sm"
                color="secondary"
                onClick={() =>
                  setChange({
                    members: [member],
                    status: accessStatus === 'waitlisted' ? 'approved' : 'waitlisted',
                  })
                }
              >
                {accessStatus === 'waitlisted' ? 'Approve access' : 'Move back to waitlist'}
              </Button>
            ) : null;
          }}
        />
        {source.data ? (
          <Pager {...source.data} onPage={(next) => navigate({ offset: next })} />
        ) : null}
      </State>
      {change ? (
        <AccessChangeDialog
          change={change}
          onClose={() => setChange(undefined)}
          onComplete={complete}
        />
      ) : null}
    </Page>
  );
}

/**
 * Member lifecycle words as the API stores them. Kept verbatim rather than
 * humanised: the same words come back in the `status` column and in the
 * operator action form, and one of the three has to be the real one.
 */
const MEMBER_STATUSES = ['invited', 'onboarding', 'active', 'paused'];

type MemberSort = NonNullable<NonNullable<ListMembersApiV1InternalMembersGetData['query']>['sort']>;

/**
 * The orders the member list can be read in. Newest members first is what the
 * server does when asked for nothing, so it is the empty choice and never
 * written to the URL or sent.
 */
const MEMBER_SORTS: Array<{ value: '' | Exclude<MemberSort, '-created'>; label: string }> = [
  { value: '', label: 'Newest members' },
  { value: 'created', label: 'Oldest members' },
  { value: '-last_login', label: 'Last login, most recent' },
  { value: 'last_login', label: 'Last login, least recent' },
  { value: '-last_active', label: 'Last active, most recent' },
  { value: 'last_active', label: 'Last active, least recent' },
];

function readMemberSort(value: string | null): '' | Exclude<MemberSort, '-created'> {
  return MEMBER_SORTS.find((option) => option.value && option.value === value)?.value ?? '';
}

/** "3 hours ago", with the exact local time on hover; "Never" for no time at all. */
function WhenSeen({ value }: { value?: string | null | undefined }) {
  if (!value) return <span className="text-tertiary">Never</span>;
  return (
    <time dateTime={value} title={formatTimestamp(value)}>
      {formatRelative(value)}
    </time>
  );
}

const PRESENTATION: Record<NonNullable<ConsumerDetail['presentation']>, string> = {
  womenswear: 'Womenswear',
  menswear: 'Menswear',
  unspecified: 'Not chosen',
};

/** Whole years from a YYYY-MM-DD date of birth, in the viewer's calendar. */
function ageFrom(dateOfBirth: string): number | null {
  const [year, month, day] = dateOfBirth.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  const today = new Date();
  let age = today.getFullYear() - year;
  if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

function YesNo({ value }: { value: boolean | undefined }) {
  return value ? <span>Yes</span> : <span className="text-tertiary">No</span>;
}

/**
 * What the member told us during onboarding about themselves, read-only. The same values are
 * editable from the profile dialog in the actions below; this shows them without opening it.
 */
function OnboardingProfile({ member }: { member: ConsumerDetail }) {
  const dob = member.date_of_birth;
  const age = dob ? ageFrom(dob) : null;
  return (
    <Section title="Profile from onboarding">
      <DetailList
        items={[
          ['Preferred name', member.preferred_name || 'Not provided'],
          [
            'Date of birth',
            dob
              ? `${new Date(`${dob.slice(0, 10)}T00:00:00`).toLocaleDateString()}${age === null ? '' : ` (${age})`}`
              : 'Not provided',
          ],
          ['Gender', member.gender ? humanize(member.gender) : 'Not provided'],
          ['Shops', member.presentation ? PRESENTATION[member.presentation] : 'Not chosen'],
          ['Height', member.height_cm ? `${member.height_cm} cm` : 'Not provided'],
          ['Weight', member.weight_kg ? `${member.weight_kg} kg` : 'Not provided'],
          ['Photos may train our styling models', <YesNo value={member.training_consent} />],
          ['Marketing messages', <YesNo value={member.marketing_consent} />],
          ['Share data with brands', <YesNo value={member.brand_sharing_consent} />],
        ]}
      />
    </Section>
  );
}

export function ConsumerList() {
  const [params, setParams] = useSearchParams();
  const query = params.get('query') ?? '';
  const status = params.get('status') ?? '';
  const requestedAssignment = params.get('assignment');
  const assignment =
    requestedAssignment === 'assigned' || requestedAssignment === 'unassigned'
      ? requestedAssignment
      : '';
  const requestedSession = params.get('onboarding_session');
  const onboardingSession =
    requestedSession === 'present' || requestedSession === 'absent' ? requestedSession : '';
  const requestedAccess = params.get('access');
  const access: '' | AccessStatus =
    requestedAccess === 'waitlisted' || requestedAccess === 'approved' ? requestedAccess : '';
  // One stylist's members. The member list has no stylist filter on the
  // server, so every matching page is read and narrowed here.
  const stylistFilter = assignment === 'unassigned' ? '' : (params.get('stylist') ?? '');
  const sort = readMemberSort(params.get('sort'));
  const offset = Number(params.get('offset') ?? 0);
  const [scheduling, setScheduling] = useState<ConsumerSummary>();
  const [stylistOptions, setStylistOptions] = useState<StylistSummary[]>([]);
  useEffect(() => {
    let live = true;
    readActiveStylists()
      .then((items) => {
        if (live) setStylistOptions(items);
      })
      .catch(() => {
        if (live) setStylistOptions([]);
      });
    return () => {
      live = false;
    };
  }, []);
  const source = useLoad<ConsumerPage>(async () => {
    if (stylistFilter) {
      const everyone = await readAllMembers({
        assignment: 'assigned',
        ...(query ? { query } : {}),
        ...(status ? { status } : {}),
        ...(onboardingSession ? { onboarding_session: onboardingSession } : {}),
        ...(access ? { access_status: access } : {}),
        ...(sort ? { sort } : {}),
      });
      const theirs = everyone.filter((member) => member.assigned_stylist_id === stylistFilter);
      return {
        items: theirs.slice(offset, offset + LIMIT),
        total: theirs.length,
        limit: LIMIT,
        offset,
        breakdown: {
          active: theirs.filter((member) => member.status === 'active').length,
          assigned: theirs.length,
        },
      };
    }
    const result = await listMembersApiV1InternalMembersGet({
      query: {
        limit: LIMIT,
        offset,
        ...(query ? { query } : {}),
        ...(status ? { status } : {}),
        ...(assignment ? { assignment } : {}),
        ...(onboardingSession ? { onboarding_session: onboardingSession } : {}),
        ...(access ? { access_status: access } : {}),
        ...(sort ? { sort } : {}),
      },
    });
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) throw new Error();
    return result.data;
  }, [query, status, assignment, onboardingSession, access, stylistFilter, sort, offset]);
  const navigate = (next: {
    query?: string;
    status?: string;
    assignment?: string;
    stylist?: string;
    onboarding_session?: string;
    access?: string;
    sort?: string;
    offset?: number;
  }) =>
    setParams({
      ...((next.query ?? query) ? { query: next.query ?? query } : {}),
      ...((next.status ?? status) ? { status: next.status ?? status } : {}),
      ...((next.assignment ?? assignment) ? { assignment: next.assignment ?? assignment } : {}),
      ...((next.stylist ?? stylistFilter) ? { stylist: next.stylist ?? stylistFilter } : {}),
      ...((next.onboarding_session ?? onboardingSession)
        ? { onboarding_session: next.onboarding_session ?? onboardingSession }
        : {}),
      ...((next.access ?? access) ? { access: next.access ?? access } : {}),
      ...((next.sort ?? sort) ? { sort: next.sort ?? sort } : {}),
      ...(next.offset ? { offset: String(next.offset) } : {}),
    });
  const narrowed = Boolean(
    query || status || assignment || stylistFilter || onboardingSession || access,
  );
  const members = source.data?.breakdown;
  return (
    <Page
      title="Consumers"
      purpose="Use this page to find a member and open their record. Their page is where you see their wardrobe, their onboarding and their conversations."
      band={
        <MetricBand
          label="The members these filters match"
          figures={[
            matchingFigure(source.data?.total, 'member', 'Members'),
            shareOfMatching({
              id: 'active',
              label: 'Status active',
              count: members?.active,
              total: source.data?.total,
              noun: 'member',
              reading: 'Members with status active',
              tone: 'success',
              hint: 'Past onboarding and not paused. Invited, onboarding and paused members are the rest.',
            }),
            shareOfMatching({
              id: 'assigned',
              label: 'Have a stylist',
              count: members?.assigned,
              total: source.data?.total,
              noun: 'member',
              reading: 'Members with a stylist assigned',
              hint: 'Their daily outfits are still generated and delivered. Without a stylist, nobody reviews or edits them first. Assign one from their own page.',
            }),
          ]}
        />
      }
      actions={
        <Button size="md" color="primary" href="/consumers/new">
          Create consumer
        </Button>
      }
      toolbar={{
        search: {
          initial: query,
          placeholder: 'Name, email, or phone',
          onSearch: (value) => navigate({ query: value, offset: 0 }),
        },
        onClear: () => setParams({}),
        canClear: narrowed,
        filters: (
          <>
            <SelectField
              label="Status"
              value={status}
              onChange={(event) => navigate({ status: event.currentTarget.value, offset: 0 })}
              options={[
                { value: '', label: 'All statuses' },
                ...MEMBER_STATUSES.map((item) => ({ value: item, label: item })),
              ]}
            />
            <SelectField
              label="Stylist"
              value={stylistFilter || assignment}
              onChange={(event) => {
                const value = event.currentTarget.value;
                if (value === '' || value === 'assigned' || value === 'unassigned')
                  navigate({ assignment: value, stylist: '', offset: 0 });
                else navigate({ assignment: '', stylist: value, offset: 0 });
              }}
              options={[
                { value: '', label: 'Assigned or not' },
                { value: 'assigned', label: 'Has a stylist' },
                { value: 'unassigned', label: 'No stylist' },
                ...(stylistFilter && !stylistOptions.some((item) => item.id === stylistFilter)
                  ? [{ value: stylistFilter, label: 'The chosen stylist' }]
                  : []),
                ...stylistOptions.map((item) => ({ value: item.id, label: item.name })),
              ]}
            />
            <SelectField
              label="Onboarding session"
              value={onboardingSession}
              onChange={(event) =>
                navigate({ onboarding_session: event.currentTarget.value, offset: 0 })
              }
              options={[
                { value: '', label: 'Booked or not' },
                { value: 'present', label: 'Session booked' },
                { value: 'absent', label: 'No session booked' },
              ]}
            />
            <SelectField
              label="Access"
              value={access}
              onChange={(event) => navigate({ access: event.currentTarget.value, offset: 0 })}
              options={[
                { value: '', label: 'Waitlisted or approved' },
                { value: 'waitlisted', label: 'Waitlisted' },
                { value: 'approved', label: 'Approved' },
              ]}
            />
            <SelectField
              label="Sort by"
              value={sort}
              onChange={(event) => navigate({ sort: event.currentTarget.value, offset: 0 })}
              options={MEMBER_SORTS}
            />
          </>
        ),
      }}
    >
      <State {...source} empty={!source.data?.items.length} onRetry={() => void source.refresh()}>
        <RecordTable
          noun="consumers"
          searchable={false}
          // A `last_login` key beside `last_login_at`: the table appends its
          // own relative time under any `_at` column, and this cell already is one.
          records={(source.data?.items ?? []).map((item) => ({
            ...item,
            last_login: item.last_login_at ?? null,
          }))}
          columns={['id', 'email', 'status', 'access_status', 'assigned_stylist_id', 'last_login']}
          rowKind="member"
          columnLabel={(column) =>
            column === 'last_login'
              ? 'Last login'
              : column === 'access_status'
                ? 'Access'
                : undefined
          }
          renderCell={(column, record) => {
            if (column === 'access_status' && typeof record['access_status'] === 'string')
              return <Status value={record['access_status']} />;
            if (column === 'assigned_stylist_id' && !record['assigned_stylist_id'])
              return <span className="text-tertiary">No stylist</span>;
            if (column === 'last_login') {
              const item = record as unknown as ConsumerSummary;
              return (
                <>
                  <WhenSeen value={item.last_login_at} />
                  {item.last_active_at && item.last_active_at !== item.last_login_at ? (
                    <span className="mt-0.5 block text-xs text-quaternary">
                      Active <WhenSeen value={item.last_active_at} />
                    </span>
                  ) : null}
                </>
              );
            }
            return undefined;
          }}
          rowActions={(record) => {
            const item = record as unknown as ConsumerSummary;
            return (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  color="secondary"
                  aria-label={`Onboarding session for ${item.name}`}
                  onClick={() => setScheduling(item)}
                >
                  Session
                </Button>
              </div>
            );
          }}
        />
        {source.data ? (
          <Pager {...source.data} onPage={(next) => navigate({ offset: next })} />
        ) : null}
      </State>
      {scheduling ? (
        <ConsumerSchedulingDialog
          consumer={scheduling}
          onClose={() => setScheduling(undefined)}
          onChanged={source.refresh}
        />
      ) : null}
    </Page>
  );
}

/* ------------------------------------------------------- operator actions */

/**
 * The two audited mutations an operator has on a person: `PATCH` on a stylist
 * and `PATCH` on a member. Both carry a required `reason`, because both are
 * written to the operator audit log, so the forms below refuse to submit
 * without one rather than letting the server answer with a 422.
 */
type ActionOutcome =
  | { status: 'idle' }
  | { status: 'forbidden' }
  | { status: 'failed'; detail: string }
  | { status: 'done'; summary: string };

/** A 403 is a permission boundary, not a failure, and never a sign-out (401 only). */
function ActionFeedback({ outcome, subject }: { outcome: ActionOutcome; subject: string }) {
  if (outcome.status === 'forbidden')
    return (
      <Callout tone="warning" title={`Your role cannot update this ${subject}.`}>
        This is a permission boundary, not a failed update — nothing was changed and you are still
        signed in. Ask an admin to widen your access.
      </Callout>
    );
  if (outcome.status === 'failed')
    return (
      <Callout tone="error" title={`This ${subject} was not updated.`}>
        <FailureText raw={outcome.detail} />
        <span className="block pt-1">
          Nothing was changed and nothing was retried automatically. Re-read the record before
          trying again.
        </span>
      </Callout>
    );
  if (outcome.status === 'done') return <Callout tone="success" title={outcome.summary} />;
  return null;
}

/** What a status change actually does, said before it is confirmed. */
const STYLIST_CONSEQUENCE: Record<'active' | 'disabled', string> = {
  disabled:
    'Disabling revokes this stylist’s access to their tools and stops any new member being assigned to them. It is the only way a stylist leaves the roster: nothing deletes one, because their past assignments, review batches and audit rows still point at this record. Members already assigned stay assigned — move them to another stylist first, from Stylist assignments or the Members list below. Reversible: activating them again restores everything.',
  active:
    'Activating restores this stylist’s access to their tools and lets members be assigned to them again.',
};

const CONSUMER_CONSEQUENCE: Record<string, string> = {
  paused:
    'Pausing stops this member’s sessions and recommendations. Their wardrobe and history are untouched, and their assigned stylist stays assigned.',
  active:
    'Activating returns this member to the normal product: sessions, recommendations and stylist contact resume.',
  onboarding:
    'Returning this member to onboarding puts them back through the onboarding flow the next time they sign in.',
  invited:
    'Returning this member to invited treats them as not yet onboarded. Use this only to correct a record that was advanced by mistake.',
};

/** The confirmation step, shown only for a status change and never for capacity alone. */
function ConfirmStep({
  title,
  body,
  busy,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Callout tone="warning" title={title}>
      <span className="block pt-1">{body}</span>
      <span className="block pt-1">This is recorded against your operator account.</span>
      <Actions>
        <Button size="md" color="secondary" isDisabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button size="md" color="primary" isDisabled={busy} onClick={onConfirm}>
          {busy ? 'Applying…' : 'Confirm change'}
        </Button>
      </Actions>
    </Callout>
  );
}

function StylistOperatorActions({
  stylist,
  onUpdated,
}: {
  stylist: StylistDetail;
  onUpdated: () => Promise<void> | void;
}) {
  const [status, setStatus] = useState('');
  const [capacity, setCapacity] = useState(String(stylist.capacity_per_day));
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ActionOutcome>({ status: 'idle' });

  const wanted = Number(capacity);
  const capacityChanged = capacity.trim() !== '' && wanted !== stylist.capacity_per_day;
  const capacityValid =
    !capacityChanged || (Number.isInteger(wanted) && wanted >= 1 && wanted <= 100);
  const statusChanged = status !== '' && status !== stylist.status;
  const ready = (statusChanged || capacityChanged) && capacityValid;

  const apply = async () => {
    // Enforced here as well as on the button: a handler that trusted the
    // disabled state would still let an unaudited change through.
    if (!ready) return;
    setBusy(true);
    setOutcome({ status: 'idle' });
    configureAdminApi();
    const result = await updateStylistApiV1InternalStylistsIdPatch({
      path: { id: stylist.id },
      body: {
        ...(statusChanged ? { status: status as 'active' | 'disabled' } : {}),
        ...(capacityChanged ? { capacity_per_day: wanted } : {}),
        reason:
          statusChanged && capacityChanged
            ? STYLIST_UPDATE_REASON.statusAndCapacity
            : statusChanged
              ? STYLIST_UPDATE_REASON.status
              : STYLIST_UPDATE_REASON.capacity,
      },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    setConfirming(false);
    if (result.response.status === 403) {
      setOutcome({ status: 'forbidden' });
      return;
    }
    if (result.error) {
      setOutcome({ status: 'failed', detail: errorDetail(result.error, 'request_failed') });
      return;
    }
    setOutcome({
      status: 'done',
      summary: [
        statusChanged ? `This stylist is now ${status}.` : undefined,
        capacityChanged ? `Daily capacity is now ${wanted}.` : undefined,
      ]
        .filter(Boolean)
        .join(' '),
    });
    setStatus('');
    await onUpdated();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready) return;
    if (statusChanged) {
      setConfirming(true);
      return;
    }
    void apply();
  };

  return (
    <FormCard
      title="Update this stylist"
      description="Activate or disable the stylist and set how many members a day they can take. Every change is audited, so a reason is required."
      onSubmit={submit}
    >
      <SelectField
        label="Status"
        value={status}
        disabled={confirming}
        onChange={(event) => {
          setStatus(event.currentTarget.value);
          setConfirming(false);
        }}
        options={[
          { value: '', label: `Leave as ${stylist.status}` },
          { value: 'active', label: 'Active' },
          { value: 'disabled', label: 'Disabled' },
        ]}
      />
      <TextField
        label="Daily capacity"
        type="number"
        min={1}
        max={100}
        value={capacity}
        disabled={confirming}
        onChange={(event) => {
          setCapacity(event.currentTarget.value);
          setConfirming(false);
        }}
        hint="How many members this stylist can be assigned in a day. Changing this alone takes effect immediately."
      />
      {capacityValid ? null : (
        <span className="text-xs text-error-primary">
          Capacity must be a whole number from 1 to 100.
        </span>
      )}
      <ActionFeedback outcome={outcome} subject="stylist" />
      {confirming && statusChanged ? (
        <ConfirmStep
          title={`Confirm ${status === 'disabled' ? 'disabling' : 'activating'} this stylist.`}
          body={STYLIST_CONSEQUENCE[status as 'active' | 'disabled']}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void apply()}
        />
      ) : (
        <Actions>
          <Button size="md" color="primary" type="submit" isDisabled={busy || !ready}>
            {busy ? 'Applying…' : statusChanged ? 'Review change' : 'Apply change'}
          </Button>
        </Actions>
      )}
    </FormCard>
  );
}

/** The value the stylist select uses for "take their stylist away". */
const UNASSIGN = 'none';

function reassignmentConsequence(to: string | undefined, from: string | null | undefined): string {
  const previous = from ? `They stop appearing in ${from}’s roster.` : '';
  if (!to)
    return `Unassigning leaves this member with no stylist at all. ${previous} Their daily outfits are still generated and delivered, but nobody reviews or edits them before they go out, and they have nobody to contact. Their wardrobe, history, booked sessions and any batch already published are untouched. Reversible: assign a stylist here again at any time.`;
  return `This member moves to ${to} straight away, and ${to} sees them in their roster from then on. ${previous} Open requests, chats and pending outfit reviews move to ${to}. Batches already published stay published, and a session already booked stays with whoever it was booked with. Daily capacity is not enforced, so nothing stops a stylist being given more members than it says; check they have room. Reversible: pick the previous stylist here and apply again.`;
}

/**
 * The member record's audited write. It carries the reassignment because
 * `assigned_stylist_id` lives on the same `PATCH`: moving a member off a
 * stylist who is being disabled should not require booking a session.
 */
/**
 * A recovery link, handed to the operator rather than to the member's inbox.
 *
 * The emailed reset already exists on the account lifecycle control. This is
 * the other half, and it exists because the members who most need a reset are
 * often the ones whose email is the reason they need one - a link sent to a
 * mailbox they cannot open is not a recovery.
 *
 * Minting retires any earlier live link for that account, so this is not a
 * read: pressing it invalidates a link somebody may already be holding. It
 * therefore confirms first, and says so.
 */
function PasswordResetLink({ id }: { id: string }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<AdminPasswordResetLink>();
  const [failure, setFailure] = useState<string>();

  const issue = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await issuePasswordResetLinkApiV1InternalAccountsIdPasswordResetLinkPost({
      path: { id },
      body: { reason: PASSWORD_RESET_LINK_REASON },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    setConfirming(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot issue a recovery link for this account.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That link could not be issued.'));
      return;
    }
    setIssued(result.data);
  };

  return (
    <Section
      title="Issue a recovery link"
      description="Mints a password reset link and shows it here instead of emailing it. Use this when the member cannot reach the mailbox the automatic email would go to."
    >
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        {issued ? (
          <Callout tone="success" title={`A live recovery link for ${issued.email}.`}>
            <div className="flex flex-col gap-2">
              <IdentifierCopy label="Recovery link" value={issued.reset_url} />
              <span className="text-xs text-tertiary">
                Expires {new Date(issued.expires_at).toLocaleString()}. Anyone holding this link can
                set this account&rsquo;s password, so send it over a channel you trust and only to
                the member. Issuing another one retires this.
              </span>
            </div>
          </Callout>
        ) : null}
        {confirming ? (
          <ConfirmStep
            title="Issue a recovery link for this member?"
            body="Any recovery link already issued for this account stops working. Whoever is holding one will find it dead."
            busy={busy}
            onCancel={() => setConfirming(false)}
            onConfirm={() => void issue()}
          />
        ) : (
          <Actions>
            <Button
              size="md"
              color="secondary"
              isDisabled={busy}
              onClick={() => setConfirming(true)}
            >
              Issue recovery link
            </Button>
          </Actions>
        )}
      </div>
    </Section>
  );
}

/**
 * Queue the onboarding reminder for a member who stalled part way through.
 *
 * What this writes is a `notification_deliveries` row. A scheduled job sends
 * queued rows as push notifications, so whether it reaches the member depends
 * on push being on and on their device; the copy says queued and stops there.
 * An operator told the member had been reminded would stop chasing them, and
 * queued is not seen.
 *
 * The 24-hour cooldown is enforced against the topic rather than this route,
 * so the automated abandonment reminder blocks an operator's nudge too. The
 * refusal names when the next one is allowed, because a button that appears
 * to do nothing teaches an operator the control is broken.
 */
function OnboardingNudge({ id }: { id: string }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState<AdminOnBehalfNudgeResult>();
  const [failure, setFailure] = useState<string>();

  const nudge = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await nudgeMemberOnboardingApiV1InternalUsersIdOnboardingNudgePost({
      path: { id },
      body: { reason: ONBOARDING_NUDGE_REASON },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    setConfirming(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot send an onboarding reminder to this member.');
      return;
    }
    if (result.response.status === 429) {
      setFailure(
        errorDetail(
          result.error,
          'This member may have been reminded automatically or by an operator within the last 24 hours, so another reminder was refused.',
        ),
      );
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That reminder could not be queued.'));
      return;
    }
    setQueued(result.data);
  };

  return (
    <Section
      title="Nudge this member’s onboarding"
      description="Queues the same onboarding reminder the automated sender uses. Queued does not mean the member saw it: it only reaches them as a push notification, if push is on for their phone. One reminder per member per 24 hours, counting the automated one."
    >
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        {queued ? (
          <Callout tone="success" title="A reminder was queued for this member.">
            <div className="flex flex-col gap-2">
              <IdentifierCopy label="Delivery" value={queued.delivery_id} />
              <IdentifierValue label="Topic" value={queued.event_type} />
              <span className="text-xs text-tertiary">
                Queued {new Date(queued.queued_at).toLocaleString()}. A scheduled job sends it as a
                push notification, so it only reaches the member if push is on for them. Queued is
                not the same as seen. The next one is allowed 24 hours from now.
              </span>
            </div>
          </Callout>
        ) : null}
        {confirming ? (
          <ConfirmStep
            title="Queue an onboarding reminder for this member?"
            body="This writes a delivery row and uses up the 24-hour window, including the one the automated abandonment reminder would otherwise have used."
            busy={busy}
            onCancel={() => setConfirming(false)}
            onConfirm={() => void nudge()}
          />
        ) : (
          <Actions>
            <Button
              size="md"
              color="secondary"
              isDisabled={busy}
              onClick={() => setConfirming(true)}
            >
              Queue onboarding reminder
            </Button>
          </Actions>
        )}
      </div>
    </Section>
  );
}

/** Every active stylist, for a picker; every page is read, not only the first. */
async function readStylistRoster(): Promise<OrForbidden<StylistSummary[]>> {
  try {
    return await readActiveStylists();
  } catch (cause) {
    if (cause instanceof ForbiddenRead) return FORBIDDEN;
    throw new Error('The stylist roster could not be loaded, so nobody can be picked here.');
  }
}

/** How a stylist reads in a picker: their roster size, and the capacity nothing enforces. */
function stylistPickerLabel(item: StylistSummary): string {
  return `${item.name} — ${item.assigned_members} ${plural(item.assigned_members, 'member')} assigned, daily capacity ${item.capacity_per_day} (not enforced)`;
}

function ConsumerOperatorActions({
  member,
  onUpdated,
}: {
  member: ConsumerDetail;
  onUpdated: () => Promise<void> | void;
}) {
  const [status, setStatus] = useState('');
  const [stylistId, setStylistId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ActionOutcome>({ status: 'idle' });

  const roster = useLoad<OrForbidden<StylistSummary[]>>(readStylistRoster, []);
  const stylists = roster.data === FORBIDDEN || !roster.data ? [] : roster.data;
  const chosen = stylists.find((item) => item.id === stylistId);

  const statusChanged = status !== '' && status !== member.status;
  const assignmentChanged =
    stylistId !== '' &&
    (stylistId === UNASSIGN
      ? Boolean(member.assigned_stylist_id)
      : stylistId !== member.assigned_stylist_id);
  const ready = statusChanged || assignmentChanged;

  const apply = async () => {
    if (!ready) return;
    setBusy(true);
    setOutcome({ status: 'idle' });
    configureAdminApi();
    const result = await updateUserApiV1InternalUsersIdPatch({
      path: { id: member.id },
      body: {
        ...(statusChanged
          ? { status: status as 'invited' | 'onboarding' | 'active' | 'paused' }
          : {}),
        ...(assignmentChanged
          ? { assigned_stylist_id: stylistId === UNASSIGN ? null : stylistId }
          : {}),
        reason:
          statusChanged && assignmentChanged
            ? MEMBER_UPDATE_REASON.statusAndAssignment
            : statusChanged
              ? MEMBER_UPDATE_REASON.status
              : MEMBER_UPDATE_REASON.assignment,
      },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    setConfirming(false);
    if (result.response.status === 403) {
      setOutcome({ status: 'forbidden' });
      return;
    }
    if (result.error) {
      setOutcome({ status: 'failed', detail: errorDetail(result.error, 'request_failed') });
      return;
    }
    setOutcome({
      status: 'done',
      summary: [
        statusChanged ? `This member is now ${status}.` : undefined,
        assignmentChanged
          ? chosen
            ? `They are now assigned to ${chosen.name}.`
            : 'They now have no stylist.'
          : undefined,
      ]
        .filter(Boolean)
        .join(' '),
    });
    setStatus('');
    setStylistId('');
    await onUpdated();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready) return;
    setConfirming(true);
  };

  const confirmTitle = statusChanged
    ? assignmentChanged
      ? `Confirm moving this member to ${status} and reassigning them.`
      : `Confirm moving this member to ${status}.`
    : chosen
      ? `Confirm reassigning this member to ${chosen.name}.`
      : 'Confirm leaving this member without a stylist.';
  const confirmBody = [
    statusChanged
      ? (CONSUMER_CONSEQUENCE[status] ?? 'This changes what the member can do in the app.')
      : undefined,
    assignmentChanged
      ? reassignmentConsequence(chosen?.name, member.assigned_stylist_name)
      : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FormCard
      title="Update this member"
      description="Move this member through their lifecycle, or move them to another stylist. Every change is audited, so a reason is required."
      onSubmit={submit}
    >
      <SelectField
        label="Status"
        value={status}
        disabled={confirming}
        onChange={(event) => {
          setStatus(event.currentTarget.value);
          setConfirming(false);
        }}
        options={[
          { value: '', label: `Leave as ${member.status}` },
          { value: 'invited', label: 'Invited' },
          { value: 'onboarding', label: 'Onboarding' },
          { value: 'active', label: 'Active' },
          { value: 'paused', label: 'Paused' },
        ]}
      />
      <SelectField
        label="Assigned stylist"
        value={stylistId}
        disabled={confirming || roster.loading}
        onChange={(event) => {
          setStylistId(event.currentTarget.value);
          setConfirming(false);
        }}
        hint="Only active stylists can be picked; each one shows how many members are already assigned to them. Reassigning here does not book a session and does not move work already published."
        options={[
          {
            value: '',
            label: member.assigned_stylist_name
              ? `Leave with ${member.assigned_stylist_name}`
              : 'Leave unassigned',
          },
          ...(member.assigned_stylist_id
            ? [{ value: UNASSIGN, label: 'Unassign — leave them with no stylist' }]
            : []),
          ...stylists
            .filter((item) => item.id !== member.assigned_stylist_id)
            .map((item) => ({
              value: item.id,
              label: stylistPickerLabel(item),
            })),
        ]}
      />
      {roster.error ? (
        <span className="text-xs text-error-primary">{roster.error}</span>
      ) : roster.data === FORBIDDEN ? (
        <span className="text-xs text-tertiary">
          Your role cannot read the stylist roster, so reassignment is not available to you.
        </span>
      ) : null}
      <ActionFeedback outcome={outcome} subject="member" />
      {confirming && (statusChanged || assignmentChanged) ? (
        <ConfirmStep
          title={confirmTitle}
          body={confirmBody}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void apply()}
        />
      ) : (
        <Actions>
          <Button size="md" color="primary" type="submit" isDisabled={busy || !ready}>
            {busy ? 'Applying…' : 'Review change'}
          </Button>
        </Actions>
      )}
    </FormCard>
  );
}

export type NotificationPrefsValues = {
  chatbot_proactive: boolean;
  daily_outfit: boolean;
  promotional: boolean;
  wardrobe_updates: boolean;
  channels: { [key: string]: string[] };
  quiet_hours: { [key: string]: string } | null;
};

/** The values as read, and whether the member has saved any or these are the defaults. */
export type NotificationPrefsRead = NotificationPrefsValues & { stored: boolean };

async function readNotificationPreferences(id: string): Promise<NotificationPrefsRead> {
  configureAdminApi();
  const result =
    await readMemberNotificationPreferencesApiV1InternalUsersIdNotificationPreferencesGet({
      path: { id },
    });
  handleUnauthorized(result.response.status);
  if (result.error || !result.data)
    throw new Error(
      errorDetail(result.error, 'This member’s notification preferences could not be read.'),
    );
  const data = result.data;
  return {
    chatbot_proactive: data.chatbot_proactive ?? false,
    daily_outfit: data.daily_outfit ?? false,
    promotional: data.promotional ?? false,
    wardrobe_updates: data.wardrobe_updates ?? false,
    channels: data.channels ?? {},
    quiet_hours: data.quiet_hours ?? null,
    stored: data.stored,
  };
}

type NotificationBooleanKey =
  'chatbot_proactive' | 'daily_outfit' | 'promotional' | 'wardrobe_updates';

const NOTIFICATION_TOGGLES: { key: NotificationBooleanKey; label: string; hint: string }[] = [
  {
    key: 'chatbot_proactive',
    label: 'Proactive chatbot notifications',
    hint: 'Allow the chatbot to start a notification without a new member message.',
  },
  {
    key: 'daily_outfit',
    label: 'Daily outfit notifications',
    hint: 'Notify this member when their daily outfit is ready.',
  },
  {
    key: 'promotional',
    label: 'Promotional notifications',
    hint: 'Allow product and promotional messages.',
  },
  {
    key: 'wardrobe_updates',
    label: 'Wardrobe update notifications',
    hint: 'Notify this member about changes to their wardrobe.',
  },
];

function NotificationPreferenceToggle({
  label,
  hint,
  value,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  value: boolean | undefined;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  const field = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const input = field.current?.querySelector('input');
    if (input) input.indeterminate = value === undefined;
  }, [value]);
  return (
    <div ref={field}>
      <CheckboxField
        label={label}
        hint={value === undefined ? `${hint} Choose enabled or disabled.` : hint}
        checked={value ?? false}
        aria-checked={value === undefined ? 'mixed' : value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </div>
  );
}

function NotificationPreferencesDialog({
  id,
  values,
  readFailed,
  onClose,
  onSaved,
}: {
  id: string;
  values: NotificationPrefsValues | undefined;
  readFailed: boolean;
  onClose: () => void;
  onSaved: (result: AdminOnBehalfActionResult) => void;
}) {
  const [booleans, setBooleans] = useState<Record<NotificationBooleanKey, boolean | undefined>>({
    chatbot_proactive: values?.chatbot_proactive,
    daily_outfit: values?.daily_outfit,
    promotional: values?.promotional,
    wardrobe_updates: values?.wardrobe_updates,
  });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const allChosen = Object.values(booleans).every((value) => value !== undefined);
  const ready = Boolean(values) && allChosen;

  const save = async () => {
    // This guard is the data-loss boundary, not merely button validation. The
    // API replaces the whole JSON blob, so no read means no safe write.
    if (!values || !allChosen) return;
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result =
      await updateMemberNotificationPreferencesApiV1InternalUsersIdNotificationPreferencesPut({
        path: { id },
        body: {
          chatbot_proactive: booleans.chatbot_proactive as boolean,
          daily_outfit: booleans.daily_outfit as boolean,
          promotional: booleans.promotional as boolean,
          wardrobe_updates: booleans.wardrobe_updates as boolean,
          channels: values.channels,
          quiet_hours: values.quiet_hours,
          reason: NOTIFICATION_PREFERENCES_REASON,
        },
      });
    handleUnauthorized(result.response.status);
    setBusy(false);
    setConfirming(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot change a member’s notification preferences.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'Those notification preferences could not be saved.'));
      return;
    }
    onSaved(result.data);
    onClose();
  };

  const unavailable = !values;
  return (
    <ActionDialog
      title="Change this member’s notification preferences"
      description="Changes which notifications this member has enabled. The member’s existing channel and quiet-hour configuration is preserved exactly."
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        {unavailable ? (
          <Callout tone="warning" title="These preferences cannot be saved safely yet.">
            The member’s current channel and quiet-hour settings cannot be read
            {readFailed ? ' because the read failed' : ''}. Writing without them would erase them,
            so submit stays disabled. There is no submit-anyway option.
          </Callout>
        ) : null}
        {NOTIFICATION_TOGGLES.map(({ key, label, hint }) => (
          <NotificationPreferenceToggle
            key={key}
            label={label}
            hint={hint}
            value={booleans[key]}
            disabled={confirming || busy}
            onChange={(value) => {
              setBooleans((current) => ({ ...current, [key]: value }));
              setConfirming(false);
            }}
          />
        ))}
        {confirming ? (
          <ConfirmStep
            title="Apply these notification preferences?"
            body="This replaces the member’s stored notification preference blob. Their channel and quiet-hour settings will be sent back unchanged alongside the four choices shown here."
            busy={busy}
            onCancel={() => setConfirming(false)}
            onConfirm={() => void save()}
          />
        ) : (
          <Actions>
            <Button size="md" color="secondary" onClick={onClose} isDisabled={busy}>
              Cancel
            </Button>
            <Button
              size="md"
              color="primary"
              isDisabled={!ready || busy}
              onClick={() => setConfirming(true)}
            >
              Review notification change
            </Button>
          </Actions>
        )}
      </div>
    </ActionDialog>
  );
}

export function MemberNotificationPreferences({
  id,
  read = readNotificationPreferences,
}: {
  id: string;
  read?: (id: string) => Promise<NotificationPrefsRead | undefined>;
}) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<AdminOnBehalfActionResult>();
  const source = useLoad(() => read(id), [id, read]);
  const current = source.data;
  return (
    <Section
      title="Member notification preferences"
      description="Which notifications this member has switched on. Changing them here keeps their existing channel and quiet-hour settings."
      actions={
        <Button
          size="sm"
          color="secondary"
          isDisabled={source.loading || !source.data}
          onClick={() => setEditing(true)}
        >
          Change notification preferences
        </Button>
      }
    >
      {saved ? (
        <Callout tone="success" title="The member’s notification preferences were updated.">
          The complete preference set was saved, including the member’s unchanged channel and
          quiet-hour settings.
        </Callout>
      ) : null}
      {current ? (
        <DetailList
          items={[
            [
              'Saved by the member',
              current.stored
                ? 'Yes'
                : 'No. Nothing is saved for this member, so these are the defaults the app uses.',
            ],
            ...NOTIFICATION_TOGGLES.map(
              ({ key, label }) => [label, current[key] ? 'On' : 'Off'] as [ReactNode, ReactNode],
            ),
            [
              'Channels',
              Object.keys(current.channels).length
                ? Object.entries(current.channels)
                    .map(
                      ([topic, channels]) => `${humanize(topic)}: ${channels.join(', ') || 'none'}`,
                    )
                    .join('; ')
                : 'None set',
            ],
            [
              'Quiet hours',
              current.quiet_hours
                ? Object.entries(current.quiet_hours)
                    .map(([field, value]) => `${humanize(field)} ${value}`)
                    .join(', ')
                : 'None set',
            ],
          ]}
        />
      ) : null}
      {!source.loading && !source.data ? (
        <Callout tone="warning" title="Notification preferences could not be read.">
          {source.error ? `${source.error} ` : ''}Writing without the member’s current channel and
          quiet-hour settings would erase them, so this control cannot submit until the read works.
        </Callout>
      ) : null}
      {editing ? (
        <NotificationPreferencesDialog
          id={id}
          values={source.data}
          readFailed={Boolean(source.error)}
          onClose={() => setEditing(false)}
          onSaved={(result) => {
            setSaved(result);
            void source.refresh();
          }}
        />
      ) : null}
    </Section>
  );
}

const OVERRIDE_MEANING =
  'This member has their own limit set for them, instead of the one everybody else gets.';

type LimitSaveState = 'idle' | 'saving' | 'saved' | 'failed';
type MemberAllowance = AdminMemberUsageLimits['allowances'][number];

const STYLIST_UPDATE_REASON = {
  status: 'Updated a stylist’s status from the admin console.',
  capacity: 'Updated a stylist’s capacity from the admin console.',
  statusAndCapacity: 'Updated a stylist’s status and capacity from the admin console.',
} as const;
const MEMBER_UPDATE_REASON = {
  status: 'Updated a member’s status from the admin console.',
  assignment: 'Reassigned a member from the admin console.',
  statusAndAssignment: 'Updated a member’s status and stylist assignment from the admin console.',
} as const;
const PASSWORD_RESET_LINK_REASON = 'Issued a member recovery link from the admin console.';
const ONBOARDING_NUDGE_REASON = 'Queued an onboarding reminder from the admin console.';
const NOTIFICATION_PREFERENCES_REASON =
  'Updated the member’s notification preferences from the admin console.';
const LOCATION_UPDATE_REASON = 'Updated the member location from the admin console.';
const ONBOARDING_CHECKPOINT_REASON =
  'Updated the member’s onboarding checkpoint from the admin console.';
const ACCOUNT_DELETION_REASON = 'Requested member account deletion from the admin console.';

function memberLimitValue(allowance: MemberAllowance): string {
  if (!allowance.override) return 'default';
  return allowance.limit === null ? 'unlimited' : String(allowance.limit);
}

/** One independently persisted member override. */
function MemberLimitControl({
  id,
  limitKey,
  label,
  period,
  allowance,
  readWired,
  overrides,
  onSaved,
}: {
  id: string;
  limitKey: string;
  label: string;
  period: string;
  allowance?: MemberAllowance;
  readWired: boolean;
  /** The member's raw stored overrides, for limits the allowance read does not cover. */
  overrides?: AdminMemberUsageLimits['member_overrides'];
  onSaved: (saved: AdminMemberUsageLimits) => void;
}) {
  const stored = overrides?.[limitKey];
  const serverValue = allowance
    ? memberLimitValue(allowance)
    : overrides
      ? stored === undefined
        ? 'default'
        : String(stored)
      : '';
  const [value, setValue] = useState(serverValue);
  const [state, setState] = useState<LimitSaveState>('idle');
  const [message, setMessage] = useState<string>();

  useEffect(() => setValue(serverValue), [serverValue]);

  const commit = async () => {
    if ((readWired && !allowance) || state === 'saving' || value === serverValue) return;
    const parsed = parseLimit(value);
    if (!parsed.ok) {
      setState('failed');
      setMessage('A whole number, “unlimited”, or “default”.');
      return;
    }

    setState('saving');
    setMessage(undefined);
    configureAdminApi();
    const result = await setMemberUsageLimitsApiV1InternalUsersIdUsageLimitsPut({
      path: { id },
      body: {
        limits: { [limitKey]: parsed.value },
        reason: `Set ${limitKey} for a member from the admin console.`,
      },
    });
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) {
      setValue(serverValue);
      setState('failed');
      setMessage(errorDetail(result.error, 'That member limit could not be saved.'));
      return;
    }
    const savedAllowance = allowance
      ? result.data.allowances.find((candidate) => candidate.key === allowance.key)
      : undefined;
    if (savedAllowance) setValue(memberLimitValue(savedAllowance));
    setState('saved');
    onSaved(result.data);
  };

  return (
    <div className="flex flex-col gap-1">
      <TextField
        label={`${label} ${period}`}
        value={value}
        disabled={(readWired && !allowance) || state === 'saving'}
        hint={
          allowance || !readWired
            ? 'Type a whole number, “unlimited”, or “default”; changes save on blur or Enter.'
            : 'Disabled because this limit was not returned by the member usage read.'
        }
        onChange={(event) => {
          setValue(event.target.value);
          setState('idle');
          setMessage(undefined);
        }}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commit();
          }
        }}
      />
      {state === 'saving' ? <span role="status">Saving {label.toLowerCase()}…</span> : null}
      {state === 'saved' ? <span role="status">Saved {label.toLowerCase()}.</span> : null}
      {state === 'failed' && message ? <FailureText raw={message} /> : null}
    </div>
  );
}

/**
 * "Why did this member stop getting outfits?" — the allowances they are held
 * to, how much of each they have spent this window, and what their generation
 * attempts actually came back as.
 */
function MemberUsageLimits({ id }: { id: string }) {
  const source = useLoad<OrForbidden<AdminMemberUsageLimits>>(async () => {
    const result = await memberUsageLimitsApiV1InternalUsersIdUsageLimitsGet({ path: { id } });
    handleUnauthorized(result.response.status);
    if (result.response.status === 403) return FORBIDDEN;
    if (result.error || !result.data)
      throw new Error('This member’s usage limits could not be loaded.');
    return result.data;
  }, [id]);
  const data = source.data === FORBIDDEN ? undefined : source.data;
  return (
    <Section
      title="What they are allowed to generate"
      description="The limits this member is held to in the current window, how much of each they have spent, and how their attempts finished. This is where an unexplained stop usually is."
      actions={
        <Actions>
          <Button size="sm" color="secondary" onClick={() => void source.refresh()}>
            Refresh
          </Button>
        </Actions>
      }
    >
      <State {...source} onRetry={() => void source.refresh()}>
        {source.data === FORBIDDEN ? (
          <PermissionBoundary subject="this member’s usage limits" />
        ) : data ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-4">
              {WRITABLE_LIMITS.map(({ key, label, period, memberReadKey }) => (
                <MemberLimitControl
                  key={key}
                  id={id}
                  limitKey={key}
                  label={label}
                  period={period}
                  readWired={Boolean(memberReadKey)}
                  {...(data.member_overrides ? { overrides: data.member_overrides } : {})}
                  allowance={
                    memberReadKey
                      ? data.allowances.find((candidate) => candidate.key === memberReadKey)
                      : undefined
                  }
                  onSaved={(saved) => source.setData(saved)}
                />
              ))}
            </div>
            <StatRow>
              <StatTile
                label="Used this window, all limits added together"
                value={
                  <Quantity
                    value={formatCount(data.total_used)}
                    unit="generations"
                    raw={data.total_used}
                  />
                }
                hint={
                  data.total_limit === null
                    ? 'A sum across every limit below. At least one of them has no limit, so there is no combined allowance. Each limit runs out on its own; read the bars below.'
                    : `A sum across every limit below, whose allowances add up to ${formatCount(data.total_limit)}. Each limit runs out on its own; read the bars below.`
                }
              />
              <StatTile
                label="Window started"
                value={new Date(data.window_start).toLocaleString()}
                hint="Usage is counted from here. Everything below resets against this date."
              />
            </StatRow>
            <div className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-4">
              {data.allowances.map((allowance) => (
                <div key={allowance.key} className="flex min-w-0 flex-col gap-1">
                  <span className="flex flex-wrap items-baseline justify-between gap-2 text-sm text-secondary">
                    <span>
                      {allowance.label}{' '}
                      {allowance.override ? (
                        <Term term="override" definition={OVERRIDE_MEANING} />
                      ) : null}
                    </span>
                    <span className="text-xs text-tertiary">
                      {allowance.limit === null ? (
                        <>
                          <Quantity
                            value={formatCount(allowance.used)}
                            unit="used"
                            raw={allowance.used}
                          />
                          , no limit set
                        </>
                      ) : (
                        <>
                          <Quantity value={formatCount(allowance.used)} raw={allowance.used} /> of{' '}
                          <Quantity
                            value={formatCount(allowance.limit)}
                            unit="allowed"
                            raw={allowance.limit}
                          />
                          ,{' '}
                          <Quantity
                            value={formatCount(allowance.remaining ?? 0)}
                            unit="left"
                            raw={allowance.remaining ?? 0}
                          />
                        </>
                      )}
                    </span>
                  </span>
                  {allowance.limit === null ? null : (
                    <Bar
                      fraction={allowance.limit > 0 ? allowance.used / allowance.limit : 0}
                      tone={(allowance.remaining ?? 0) <= 0 ? 'error' : 'success'}
                      label={`${allowance.label} used against the limit: ${allowance.used} of ${allowance.limit}`}
                    />
                  )}
                </div>
              ))}
            </div>
            {data.outcomes.some(
              (outcome) =>
                (outcome.completed ?? 0) + (outcome.failed ?? 0) + (outcome.pending ?? 0) > 0,
            ) ? (
              <RecordTable
                noun="generation outcomes"
                searchable={false}
                records={data.outcomes}
                columns={['key', 'completed', 'pending', 'failed']}
                emptyHint="This member has not attempted a generation in this window."
              />
            ) : (
              <Callout tone="neutral" title="No generation attempts in this window.">
                The read succeeded and found nothing. A member who has not asked for anything looks
                exactly like this, so it is an answer rather than a failure.
              </Callout>
            )}
            {data.unverifiable_keys?.includes('outfit_regen') ? (
              <p className="text-sm text-tertiary">
                Outfit regenerations aren’t tracked per outcome, so they don’t appear in the
                attempts above.
              </p>
            ) : null}
          </div>
        ) : null}
      </State>
    </Section>
  );
}

/**
 * Re-running the member's paid generations from the console.
 *
 * The member-wide daily-look action stays here; outfit-scoped regeneration
 * and try-on actions live beside the outfit they act on. This run costs real
 * money, so it states its consequence and is pressed twice. It does not spend
 * the member's own allowance - an operator fixing something broken must not
 * bill the member for the fix.
 *
 * The result is reported as queued, never as done. This is a multi-minute
 * out-of-band pipeline; the 202 names the work, and the member's own screens
 * are where the output eventually turns up.
 */
type GenerationAction = 'daily-look';

const GENERATION_REASON: Record<GenerationAction, string> = {
  'daily-look': 'Regenerated a member’s daily look from the admin console.',
};

const GENERATION: Record<
  GenerationAction,
  {
    button: string;
    title: string;
    description: string;
    consequence: string;
    confirm: string;
  }
> = {
  'daily-look': {
    button: 'Regenerate today’s look',
    title: 'Re-run this member’s Outfit of the Day',
    description:
      'Queues the Outfit-of-the-Day pipeline for this member. What comes back names the queued work, not a finished look.',
    consequence:
      'Re-runs the member’s Outfit-of-the-Day pipeline. This spends money on the language and image models every time it is pressed. The pick already cached is always discarded: the route forces a fresh attempt, so there is no answer where this costs nothing.',
    confirm: 'Confirm regeneration',
  },
};

function GenerationDialog({
  id,
  action,
  onClose,
  onAccepted,
}: {
  id: string;
  action: GenerationAction;
  onClose: () => void;
  onAccepted: (accepted: AdminOnBehalfJobAccepted) => void;
}) {
  const copy = GENERATION[action];
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  // Minted once, when the dialog opens, rather than on each render: a browser
  // that retries the request must present the key the first attempt used, or
  // the retry is a second paid pipeline run. A genuinely new attempt reopens
  // the dialog, which mints a new one.
  const [idempotencyKey] = useState(() => `admin-${action}:${crypto.randomUUID()}`);

  const run = async () => {
    // The button is already disabled without these; the guard is here because
    // an unaudited paid run must not be one stray submit away.
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const path = { id };
    const headers = { 'Idempotency-Key': idempotencyKey };
    const result = await regenerateMemberDailyLookApiV1InternalUsersIdDailyLookRegeneratePost({
      path,
      headers,
      // Always forced: the route regenerates precisely because the
      // operator has seen the cached pick and found it wrong.
      body: { reason: GENERATION_REASON[action], force: true },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    setConfirming(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot run a generation on a member’s behalf.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That generation could not be queued.'));
      return;
    }
    onAccepted(result.data);
    onClose();
  };

  return (
    <ActionDialog title={copy.title} description={copy.description} onClose={onClose}>
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        <span className="text-sm text-secondary">{copy.consequence}</span>
        {confirming ? (
          <Actions>
            <Button
              size="md"
              color="secondary"
              isDisabled={busy}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button size="md" color="primary" isDisabled={busy} onClick={() => void run()}>
              {busy ? 'Queueing…' : copy.confirm}
            </Button>
          </Actions>
        ) : (
          <Actions>
            <Button
              size="md"
              color="secondary"
              isDisabled={busy}
              onClick={() => setConfirming(true)}
            >
              Review this run
            </Button>
          </Actions>
        )}
      </div>
    </ActionDialog>
  );
}

/**
 * "Their look is wrong, run it again" - the three paid pipelines an operator
 * can start for a member, next to the allowances that say what the member
 * could have started themselves.
 */
function MemberGenerationActions({ id }: { id: string }) {
  const [action, setAction] = useState<GenerationAction>();
  const [accepted, setAccepted] = useState<{
    action: GenerationAction;
    job: AdminOnBehalfJobAccepted;
  }>();

  return (
    <Section
      title="Run a generation for this member"
      description="Starts the member’s own pipelines on their behalf, without spending their allowance. Each of these costs real money, so each is confirmed separately."
      actions={
        <Actions>
          {(Object.keys(GENERATION) as GenerationAction[]).map((key) => (
            <Button key={key} size="sm" color="secondary" onClick={() => setAction(key)}>
              {GENERATION[key].button}
            </Button>
          ))}
        </Actions>
      }
    >
      <div className="flex flex-col gap-4">
        {accepted ? (
          <Callout
            tone="success"
            title={`${GENERATION[accepted.action].button} was queued, not completed.`}
          >
            <div className="flex flex-col gap-2">
              <span className="block pt-1">
                The pipeline runs out of band and takes minutes. Nothing here changes when it
                finishes; re-read the member’s own screens for the result.
              </span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-xs text-tertiary">Member</span>
                <EntityRef kind="member" id={accepted.job.member_id} />
              </span>
              <IdentifierValue label="Queue status" value={accepted.job.status} />
              {accepted.job.reference && looksLikeUuid(accepted.job.reference) ? (
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-xs text-tertiary">Queued job</span>
                  <EntityRef kind="workflow_job" id={accepted.job.reference} />
                </span>
              ) : accepted.job.reference ? (
                <IdentifierCopy label="Reference" value={accepted.job.reference} />
              ) : (
                <IdentifierValue label="Reference" value="None returned for this job." />
              )}
              <span className="text-xs text-tertiary">
                {accepted.job.quota_consumed
                  ? 'This did claim one of the member’s own attempts.'
                  : 'This cost the member nothing from their own allowance.'}
              </span>
            </div>
          </Callout>
        ) : null}
        {action ? (
          <GenerationDialog
            // Remounting per action mints that action its own idempotency key.
            key={action}
            id={id}
            action={action}
            onClose={() => setAction(undefined)}
            onAccepted={(job) => setAccepted({ action, job })}
          />
        ) : null}
      </div>
    </Section>
  );
}

type ProfileArea = 'personal' | 'privacy' | 'presentation' | 'measurements';

const PROFILE_UPDATE_REASON: Record<ProfileArea, string> = {
  personal: 'Updated a member’s personal profile from the admin console.',
  privacy: 'Updated a member’s privacy choices from the admin console.',
  presentation: 'Updated a member’s clothing presentation from the admin console.',
  measurements: 'Updated a member’s measurements from the admin console.',
};

const PROFILE_AREA: Record<ProfileArea, { button: string; title: string; description: string }> = {
  personal: {
    button: 'Edit personal details',
    title: 'Edit this member’s personal details',
    description:
      'Saves only the fields you change. City and zone are managed by the dedicated location action.',
  },
  privacy: {
    button: 'Edit privacy choices',
    title: 'Edit this member’s privacy choices',
    description:
      'Privacy updates replace all three consent choices. The current choices are loaded first so unchanged consent is preserved.',
  },
  presentation: {
    button: 'Edit presentation',
    title: 'Edit this member’s presentation',
    description:
      'Changes which clothing presentation the member sees in the product. The current value is loaded before editing.',
  },
  measurements: {
    button: 'Edit measurements',
    title: 'Edit this member’s measurements',
    description:
      'Saves only the measurements you change. Clearing a changed measurement clears that value.',
  },
};

const commaList = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

function ProfileDialog({
  id,
  member,
  area,
  onClose,
  onSaved,
}: {
  id: string;
  member: ConsumerDetail;
  area: ProfileArea;
  onClose: () => void;
  onSaved: (result: AdminOnBehalfProfileResult) => Promise<void>;
}) {
  const copy = PROFILE_AREA[area];
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [preferredName, setPreferredName] = useState(member.preferred_name || member.name);
  const [email, setEmail] = useState(member.email ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(member.date_of_birth ?? '');
  const [gender, setGender] = useState(member.gender ?? '');
  const [trainingConsent, setTrainingConsent] = useState(member.training_consent ?? false);
  const [marketingConsent, setMarketingConsent] = useState(member.marketing_consent ?? false);
  const [brandSharingConsent, setBrandSharingConsent] = useState(
    member.brand_sharing_consent ?? false,
  );
  const [presentation, setPresentation] = useState(member.presentation ?? 'unspecified');
  const [heightCm, setHeightCm] = useState(member.height_cm?.toString() ?? '');
  const [weightKg, setWeightKg] = useState(member.weight_kg?.toString() ?? '');
  const [age, setAge] = useState(member.measurement_age?.toString() ?? '');
  const [measurementGender, setMeasurementGender] = useState(member.measurement_gender ?? '');
  const [changed, setChanged] = useState<Set<string>>(() => new Set());
  const markChanged = (field: string) => setChanged((current) => new Set(current).add(field));
  const ready = changed.size > 0;

  const save = async () => {
    if (!ready) return;
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const path = { id };
    const auditReason = PROFILE_UPDATE_REASON[area];
    const result =
      area === 'personal'
        ? await editMemberPersonalProfileApiV1InternalUsersIdProfilePersonalPut({
            path,
            body: {
              ...(changed.has('preferred_name') ? { preferred_name: preferredName.trim() } : {}),
              ...(changed.has('email') ? { email: email.trim() } : {}),
              ...(changed.has('date_of_birth') ? { date_of_birth: dateOfBirth } : {}),
              ...(changed.has('gender')
                ? { gender: gender as '' | 'female' | 'male' | 'nonbinary' | 'prefer_not' }
                : {}),
              reason: auditReason,
            },
          })
        : area === 'privacy'
          ? await editMemberProfilePrivacyApiV1InternalUsersIdProfilePrivacyPut({
              path,
              body: {
                training_consent: trainingConsent,
                marketing_consent: marketingConsent,
                brand_sharing_consent: brandSharingConsent,
                reason: auditReason,
              },
            })
          : area === 'presentation'
            ? await editMemberProfilePresentationApiV1InternalUsersIdProfilePresentationPut({
                path,
                body: {
                  presentation,
                  reason: auditReason,
                },
              })
            : await editMemberProfileMeasurementsApiV1InternalUsersIdProfileMeasurementsPut({
                path,
                body: {
                  ...(changed.has('height_cm')
                    ? { height_cm: heightCm ? Number(heightCm) : null }
                    : {}),
                  ...(changed.has('weight_kg')
                    ? { weight_kg: weightKg ? Number(weightKg) : null }
                    : {}),
                  ...(changed.has('age') ? { age: age ? Number(age) : null } : {}),
                  ...(changed.has('measurement_gender')
                    ? {
                        gender: measurementGender ? (measurementGender as 'male' | 'female') : null,
                      }
                    : {}),
                  reason: auditReason,
                },
              });
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot edit this member’s profile.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That profile change could not be saved.'));
      return;
    }
    await onSaved(result.data);
    onClose();
  };

  return (
    <ActionDialog title={copy.title} description={copy.description} onClose={onClose}>
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        {area === 'personal' ? (
          <>
            <TextField
              label="Preferred name"
              maxLength={200}
              value={preferredName}
              onChange={(event) => {
                setPreferredName(event.target.value);
                markChanged('preferred_name');
              }}
            />
            <TextField
              label="Profile email"
              type="email"
              maxLength={320}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                markChanged('email');
              }}
            />
            <TextField
              label="Date of birth"
              type="date"
              value={dateOfBirth}
              onChange={(event) => {
                setDateOfBirth(event.target.value);
                markChanged('date_of_birth');
              }}
            />
            <SelectField
              label="Gender"
              value={gender}
              onChange={(event) => {
                setGender(event.target.value);
                markChanged('gender');
              }}
              options={[
                { value: '', label: 'Not specified' },
                { value: 'female', label: 'Female' },
                { value: 'male', label: 'Male' },
                { value: 'nonbinary', label: 'Non-binary' },
                { value: 'prefer_not', label: 'Prefer not to say' },
              ]}
            />
          </>
        ) : area === 'privacy' ? (
          <>
            <NotificationPreferenceToggle
              label="Consent to model training"
              hint="Allow de-identified member data to improve models."
              value={trainingConsent}
              disabled={busy}
              onChange={(value) => {
                setTrainingConsent(value);
                markChanged('training_consent');
              }}
            />
            <NotificationPreferenceToggle
              label="Consent to marketing"
              hint="Allow marketing communication."
              value={marketingConsent}
              disabled={busy}
              onChange={(value) => {
                setMarketingConsent(value);
                markChanged('marketing_consent');
              }}
            />
            <NotificationPreferenceToggle
              label="Consent to sharing with brands"
              hint="Allow aggregate trend sharing with brands."
              value={brandSharingConsent}
              disabled={busy}
              onChange={(value) => {
                setBrandSharingConsent(value);
                markChanged('brand_sharing_consent');
              }}
            />
          </>
        ) : area === 'presentation' ? (
          <SelectField
            label="Clothing presentation"
            value={presentation}
            onChange={(event) => {
              setPresentation(event.target.value as 'womenswear' | 'menswear' | 'unspecified');
              markChanged('presentation');
            }}
            options={[
              { value: 'unspecified', label: 'Unspecified' },
              { value: 'womenswear', label: 'Womenswear' },
              { value: 'menswear', label: 'Menswear' },
            ]}
          />
        ) : (
          <>
            <TextField
              label="Height in centimetres"
              type="number"
              min={120}
              max={210}
              step="any"
              value={heightCm}
              onChange={(event) => {
                setHeightCm(event.target.value);
                markChanged('height_cm');
              }}
            />
            <TextField
              label="Weight in kilograms"
              type="number"
              min={35}
              max={150}
              step="any"
              value={weightKg}
              onChange={(event) => {
                setWeightKg(event.target.value);
                markChanged('weight_kg');
              }}
            />
            <TextField
              label="Age"
              type="number"
              min={16}
              max={80}
              step={1}
              value={age}
              onChange={(event) => {
                setAge(event.target.value);
                markChanged('age');
              }}
            />
            <SelectField
              label="Measurement profile gender"
              value={measurementGender}
              onChange={(event) => {
                setMeasurementGender(event.target.value);
                markChanged('measurement_gender');
              }}
              options={[
                { value: '', label: 'Not specified' },
                { value: 'female', label: 'Female' },
                { value: 'male', label: 'Male' },
              ]}
            />
          </>
        )}
        <Actions>
          <Button size="md" color="secondary" isDisabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button size="md" color="primary" isDisabled={busy || !ready} onClick={() => void save()}>
            Save profile change
          </Button>
        </Actions>
      </div>
    </ActionDialog>
  );
}

function MemberProfileActions({
  id,
  member,
  onSaved,
}: {
  id: string;
  member: ConsumerDetail;
  onSaved: (message: string) => Promise<void>;
}) {
  const [area, setArea] = useState<ProfileArea>();
  return (
    <>
      {(Object.keys(PROFILE_AREA) as ProfileArea[]).map((key) => (
        <Button key={key} size="sm" color="secondary" onClick={() => setArea(key)}>
          {PROFILE_AREA[key].button}
        </Button>
      ))}
      {area ? (
        <ProfileDialog
          key={area}
          id={id}
          member={member}
          area={area}
          onClose={() => setArea(undefined)}
          onSaved={(saved) => onSaved(`Member profile updated (${saved.capability}).`)}
        />
      ) : null}
    </>
  );
}

function LocationDialog({
  id,
  member,
  onClose,
  onSaved,
}: {
  id: string;
  member: ConsumerDetail;
  onClose: () => void;
  onSaved: (result: AdminOnBehalfProfileResult) => Promise<void>;
}) {
  const [city, setCity] = useState(member.city ?? '');
  const [zone, setZone] = useState(member.zone ?? '');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const ready = city.trim().length > 0;
  const save = async () => {
    if (!ready) return;
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await setMemberProfileLocationApiV1InternalUsersIdProfileLocationPut({
      path: { id },
      body: { city: city.trim(), zone: zone.trim(), reason: LOCATION_UPDATE_REASON },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot set this member’s profile location.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That location could not be saved.'));
      return;
    }
    await onSaved(result.data);
    onClose();
  };
  return (
    <ActionDialog
      title="Set this member’s profile location"
      description="Stores the city and product zone directly. This does not use coordinates or reverse geocoding."
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        <TextField
          label="City"
          maxLength={200}
          required
          value={city}
          onChange={(event) => setCity(event.target.value)}
        />
        <TextField
          label="Zone"
          maxLength={200}
          value={zone}
          onChange={(event) => setZone(event.target.value)}
        />
        <Actions>
          <Button size="md" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="md" color="primary" isDisabled={!ready || busy} onClick={() => void save()}>
            Save location change
          </Button>
        </Actions>
      </div>
    </ActionDialog>
  );
}

function MemberLocation({
  id,
  member,
  onSaved,
}: {
  id: string;
  member: ConsumerDetail;
  onSaved: (message: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <>
      <Button size="sm" color="secondary" onClick={() => setEditing(true)}>
        Set city and zone
      </Button>
      {editing ? (
        <LocationDialog
          id={id}
          member={member}
          onClose={() => setEditing(false)}
          onSaved={() => onSaved('The member’s profile location was updated.')}
        />
      ) : null}
    </>
  );
}

const ONBOARDING_CHECKPOINTS = [
  'about',
  'gender',
  'clothingSection',
  'birthday',
  'expectations',
  'frustrations',
  'brandsLiked',
  'brandsAvoided',
  'shape',
  'lengths',
  'swipeIntro',
  'outfitSets',
  'sleeves',
  'necklines',
  'shoppingFrequency',
  'shoppingBudget',
  'worklifeIntro',
  'workStatus',
  'profession',
  'workMode',
  'workdays',
  'dayOffs',
  'weekdayActivities',
  'weekendActivities',
  'workOutfits',
  'colourIntro',
  'colourConfirm',
  'colourResult',
  'photo',
  'twinReady',
  'bodyScanIntro',
  'bodyScanDetails',
  'bodyScanReady',
  'bodyScanFrontInstructions',
  'bodyScanSideInstructions',
  'permission',
] as const;

function OnboardingCheckpointDialog({
  id,
  member,
  onClose,
  onSaved,
}: {
  id: string;
  member: ConsumerDetail;
  onClose: () => void;
  onSaved: (result: AdminOnBehalfOnboardingResult) => Promise<void>;
}) {
  const [step, setStep] = useState(member.onboarding_checkpoint ?? 'about');
  const [nextStep, setNextStep] = useState(member.onboarding_checkpoint ?? 'about');
  const [completedSteps, setCompletedSteps] = useState(
    (member.onboarding_completed_steps ?? []).join(', '),
  );
  const [expectedRevision, setExpectedRevision] = useState(
    member.onboarding_revision?.toString() ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const save = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await saveMemberOnboardingCheckpointApiV1InternalUsersIdOnboardingCheckpointPut({
      path: { id },
      body: {
        step,
        next_step: nextStep,
        completed_steps: commaList(completedSteps),
        ...(expectedRevision ? { expected_revision: Number(expectedRevision) } : {}),
        reason: ONBOARDING_CHECKPOINT_REASON,
      },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role needs the onboarding-operate permission to change a checkpoint.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That onboarding checkpoint could not be saved.'));
      return;
    }
    await onSaved(result.data);
    onClose();
  };
  const options = ONBOARDING_CHECKPOINTS.map((value) => ({ value, label: humanize(value) }));
  return (
    <ActionDialog
      title="Change this member’s onboarding checkpoint"
      description="Writes the checkpoint the app resumes from. Unlike a nudge, this changes onboarding state; it does not queue a reminder."
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        <SelectField
          label="Step being saved"
          value={step}
          onChange={(event) => setStep(event.target.value)}
          options={options}
        />
        <SelectField
          label="Next step"
          value={nextStep}
          onChange={(event) => setNextStep(event.target.value)}
          options={[...options, { value: 'done', label: 'Done' }]}
        />
        <TextField
          label="Completed steps"
          hint="Optional, comma-separated checkpoint names. This list replaces the stored completed-step list."
          value={completedSteps}
          onChange={(event) => setCompletedSteps(event.target.value)}
        />
        <TextField
          label="Expected revision"
          type="number"
          min={0}
          step={1}
          hint="Optional. If the member has moved since this revision, the server refuses the write instead of overwriting it."
          value={expectedRevision}
          onChange={(event) => setExpectedRevision(event.target.value)}
        />
        <Actions>
          <Button size="md" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="md" color="primary" isDisabled={busy} onClick={() => void save()}>
            Save checkpoint change
          </Button>
        </Actions>
      </div>
    </ActionDialog>
  );
}

function MemberOnboardingCheckpoint({
  id,
  member,
  onSaved,
}: {
  id: string;
  member: ConsumerDetail;
  onSaved: (message: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <>
      <Button size="sm" color="secondary" onClick={() => setEditing(true)}>
        Change checkpoint
      </Button>
      {editing ? (
        <OnboardingCheckpointDialog
          id={id}
          member={member}
          onClose={() => setEditing(false)}
          onSaved={(saved) =>
            onSaved(
              `The member’s onboarding checkpoint was saved (${saved.checkpoint}, revision ${saved.revision}).`,
            )
          }
        />
      ) : null}
    </>
  );
}

function MemberActions({
  id,
  member,
  onUpdated,
}: {
  id: string;
  member: ConsumerDetail;
  onUpdated: () => Promise<void>;
}) {
  const [notice, setNotice] = useState<string>();
  const saved = async (message: string) => {
    await onUpdated();
    setNotice(message);
  };
  return (
    <Section
      title="Act on this member"
      description="Profile, location, and onboarding actions. Each write is recorded against your operator account. Twin replacement is available beside the twin record on the Onboarding tab."
      actions={
        <Actions>
          <MemberProfileActions id={id} member={member} onSaved={saved} />
          <MemberLocation id={id} member={member} onSaved={saved} />
          <MemberOnboardingCheckpoint id={id} member={member} onSaved={saved} />
        </Actions>
      }
    >
      {notice ? <Callout tone="success" title={notice} /> : null}
    </Section>
  );
}

type LifecycleAction = 'export' | 'deletion';

const DATA_EXPORT_REQUEST_REASON = 'Requested the member’s data export from the admin console.';
const DATA_EXPORT_DOWNLOAD_REASON = 'Retrieved the member’s data export from the admin console.';

function LifecycleDialog({
  id,
  action,
  onClose,
  onAccepted,
}: {
  id: string;
  action: LifecycleAction;
  onClose: () => void;
  onAccepted: (result: AdminOnBehalfLifecycleAccepted) => void;
}) {
  const deletion = action === 'deletion';
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [idempotencyKey] = useState(() => `admin-${action}:${crypto.randomUUID()}`);
  const request = async () => {
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const options = {
      path: { id },
      headers: { 'Idempotency-Key': idempotencyKey },
      body: { reason: deletion ? ACCOUNT_DELETION_REASON : DATA_EXPORT_REQUEST_REASON },
    };
    const result = deletion
      ? await requestMemberAccountDeletionApiV1InternalUsersIdDeletionRequestPost(options)
      : await requestMemberDataExportApiV1InternalUsersIdDataExportPost(options);
    handleUnauthorized(result.response.status);
    setBusy(false);
    setConfirming(false);
    if (result.response.status === 403) {
      setFailure(
        deletion
          ? 'Requesting account deletion needs a destructive-actions grant, which your role does not hold.'
          : 'Your role cannot request a data export for this member.',
      );
      return;
    }
    if (result.error || !result.data) {
      setFailure(
        errorDetail(
          result.error,
          deletion
            ? 'That account deletion request could not be queued.'
            : 'That data export could not be queued.',
        ),
      );
      return;
    }
    onAccepted(result.data);
    onClose();
  };
  return (
    <ActionDialog
      title={
        deletion ? 'Request deletion of this member’s account' : 'Request this member’s data export'
      }
      description={
        deletion
          ? 'Soft-deletes the account and starts its 30-day grace period. Restoration is a separate audited action during that window.'
          : 'Queues an export of the member’s data for the member to retrieve when processing finishes.'
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        <Callout tone="info" title="This request is recorded against your operator account.">
          The audit reason records that the {deletion ? 'deletion' : 'export'} was requested from
          the admin console.
        </Callout>
        {confirming ? (
          <ConfirmStep
            title={deletion ? 'Request account deletion?' : 'Queue this data export?'}
            body={
              deletion
                ? 'The account is soft-deleted and enters a 30-day grace period. The member loses normal access; restoration requires a separate audited action before permanent deletion.'
                : 'This queues an asynchronous export. The member retrieves it after the export service finishes processing it.'
            }
            busy={busy}
            onCancel={() => setConfirming(false)}
            onConfirm={() => void request()}
          />
        ) : (
          <Actions>
            <Button size="md" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="md"
              color={deletion ? 'primary-destructive' : 'primary'}
              isDisabled={busy}
              onClick={() => setConfirming(true)}
            >
              {deletion ? 'Review deletion request' : 'Review export request'}
            </Button>
          </Actions>
        )}
      </div>
    </ActionDialog>
  );
}

function MemberDataExport({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState<AdminOnBehalfLifecycleAccepted>();
  const [exportId, setExportId] = useState('');
  const [status, setStatus] = useState<PrivacyExportResponse>();
  const [download, setDownload] = useState<PrivacyExportDownload>();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();

  const loadStatus = async () => {
    if (!exportId.trim()) return;
    setBusy(true);
    setFailure(undefined);
    setDownload(undefined);
    configureAdminApi();
    const result = await memberDataExportStatusApiV1InternalUsersIdDataExportsExportIdGet({
      path: { id, export_id: exportId.trim() },
    });
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot read a member’s data-export status.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That data-export status could not be loaded.'));
      return;
    }
    setStatus(result.data);
  };

  const prepareDownload = async () => {
    if (!exportId.trim()) return;
    setBusy(true);
    setFailure(undefined);
    configureAdminApi();
    const result = await downloadMemberDataExportApiV1InternalUsersIdDataExportsExportIdDownloadGet(
      {
        path: { id, export_id: exportId.trim() },
        query: { reason: DATA_EXPORT_DOWNLOAD_REASON },
      },
    );
    handleUnauthorized(result.response.status);
    setBusy(false);
    if (result.response.status === 403) {
      setFailure('Your role cannot retrieve a member’s data export.');
      return;
    }
    if (result.error || !result.data) {
      setFailure(errorDetail(result.error, 'That data export could not be retrieved.'));
      return;
    }
    setDownload(result.data);
  };

  const accept = (result: AdminOnBehalfLifecycleAccepted) => {
    setAccepted(result);
    setExportId(result.request_id);
    setStatus(undefined);
    setDownload(undefined);
  };

  const statusTone =
    status?.status === 'ready'
      ? 'success'
      : status?.status === 'failed' || status?.status === 'expired'
        ? 'error'
        : 'info';
  return (
    <Section
      title="Member data export"
      description="Request an export, check an existing export id, and retrieve the file when the server reports it ready. These actions are recorded against your operator account."
      actions={
        <Button size="sm" color="secondary" onClick={() => setOpen(true)}>
          Request export
        </Button>
      }
    >
      {accepted ? (
        <Callout tone="success" title="The data export was queued, not completed.">
          <div className="flex flex-col gap-2">
            <IdentifierCopy label="Export" value={accepted.request_id} />
            <IdentifierValue label="Status" value={accepted.status} />
          </div>
        </Callout>
      ) : null}
      <div className="flex flex-col gap-4">
        {failure ? <FailureText raw={failure} /> : null}
        <TextField
          label="Export id"
          hint="Paste an earlier export id here, or request a new export above."
          value={exportId}
          onChange={(event) => {
            setExportId(event.target.value);
            setStatus(undefined);
            setDownload(undefined);
          }}
        />
        <Actions>
          <Button
            size="sm"
            color="secondary"
            isDisabled={!exportId.trim() || busy}
            onClick={() => void loadStatus()}
          >
            {busy ? 'Working…' : 'Load export status'}
          </Button>
          {status?.status === 'ready' && status.download_available !== false ? (
            <Button
              size="sm"
              color="secondary"
              isDisabled={busy}
              onClick={() => void prepareDownload()}
            >
              Prepare download
            </Button>
          ) : null}
        </Actions>
        {status ? (
          <Callout tone={statusTone} title={`Export status: ${humanize(status.status)}`}>
            <div className="flex flex-col gap-2">
              <IdentifierCopy label="Export" value={status.id} />
              <IdentifierValue
                label="Requested"
                value={new Date(status.requested_at).toLocaleString()}
              />
              {status.completed_at ? (
                <IdentifierValue
                  label="Completed"
                  value={new Date(status.completed_at).toLocaleString()}
                />
              ) : null}
              {status.error_code ? (
                <IdentifierValue label="Error code" value={status.error_code} />
              ) : null}
            </div>
          </Callout>
        ) : null}
        {download ? (
          <Callout tone="success" title="The server issued a time-limited download link.">
            <div className="flex flex-col gap-2">
              <a
                className="text-sm font-semibold text-brand-secondary underline"
                href={download.download_url}
              >
                Download member data export
              </a>
              <span className="text-sm text-tertiary">
                Link expires {new Date(download.expires_at).toLocaleString()}.
              </span>
            </div>
          </Callout>
        ) : null}
      </div>
      {open ? (
        <LifecycleDialog
          id={id}
          action="export"
          onClose={() => setOpen(false)}
          onAccepted={accept}
        />
      ) : null}
    </Section>
  );
}

function MemberAccountDeletion({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState<AdminOnBehalfLifecycleAccepted>();
  return (
    <Section
      title="Request account deletion"
      description="Soft-deletes the member’s account and starts a 30-day grace period. This requires the destructive-actions permission."
      actions={
        <Button size="sm" color="primary-destructive" onClick={() => setOpen(true)}>
          Request account deletion
        </Button>
      }
    >
      {accepted ? (
        <Callout tone="warning" title="The account deletion request was accepted.">
          <div className="flex flex-col gap-2">
            <IdentifierCopy label="Deletion request" value={accepted.request_id} />
            <IdentifierValue label="Status" value={accepted.status} />
            <span className="text-xs text-tertiary">
              The 30-day grace period has started. Use the deletion-request restoration control to
              reverse it during that window.
            </span>
          </div>
        </Callout>
      ) : null}
      {open ? (
        <LifecycleDialog
          id={id}
          action="deletion"
          onClose={() => setOpen(false)}
          onAccepted={setAccepted}
        />
      ) : null}
    </Section>
  );
}

export function ConsumerDetails() {
  const { id = '' } = useParams();
  const source = useLoad<ConsumerDetail>(async () => {
    const result = await getUserApiV1InternalUsersIdGet({ path: { id } });
    handleUnauthorized(result.response.status);
    if (result.response.status === 405) {
      for (let offset = 0; offset < 2_000; offset += 100) {
        const fallback = await listMembersApiV1InternalMembersGet({
          query: { limit: 100, offset },
        });
        handleUnauthorized(fallback.response.status);
        if (fallback.error || !fallback.data) break;
        const match = fallback.data.items.find((item) => item.id === id);
        if (match) return match;
        if (offset + fallback.data.items.length >= fallback.data.total) break;
      }
      throw new Error(
        'Consumer detail is awaiting the Admin backend deployment. Return to Consumers or refresh after deployment.',
      );
    }
    if (result.error || !result.data) throw new Error('Consumer details could not be loaded.');
    return result.data;
  }, [id]);
  const [params, setParams] = useSearchParams();
  const tab = resolveInsightTab(params.get('tab'));
  const selectTab = (next: InsightTabId) => setParams({ tab: next }, { replace: true });
  return (
    <Page
      title={source.data?.name ?? 'Consumer details'}
      purpose="This member's record, lifecycle controls, and everything the product has recorded about their journey."
      actions={
        <Button size="md" color="secondary" href="/consumers">
          Back to consumers
        </Button>
      }
    >
      <MemberDeletionBanner id={id} />
      <MemberInsights
        id={id}
        tab={tab}
        onTabChange={selectTab}
        memberName={source.data?.name}
        member={source.data}
        overview={
          <State {...source} onRetry={() => void source.refresh()}>
            {source.data ? (
              <div className="flex flex-col gap-8">
                <Section title="Consumer record">
                  <DetailList
                    items={[
                      ['Email', source.data.email ?? 'Not provided'],
                      ['Phone', source.data.phone ?? 'Not provided'],
                      ['Status', <Status value={source.data.status} />],
                      ['Access', <Status value={source.data.access_status} />],
                      [
                        'How they signed up',
                        SIGNUP_SOURCE[source.data.signup_source] ??
                          humanize(source.data.signup_source),
                      ],
                      [
                        'Assigned stylist',
                        source.data.assigned_stylist_id ? (
                          <EntityRef
                            kind="stylist"
                            id={source.data.assigned_stylist_id}
                            label={source.data.assigned_stylist_name}
                          />
                        ) : (
                          (source.data.assigned_stylist_name ?? 'Unassigned')
                        ),
                      ],
                      [
                        'Residence',
                        [source.data.building, source.data.apartment].filter(Boolean).join(', ') ||
                          'Not provided',
                      ],
                      ['City', source.data.city || 'Not provided'],
                      ['Zone', source.data.zone || 'Not provided'],
                      ['Member since', new Date(source.data.created_at).toLocaleString()],
                      ['Last login', <WhenSeen value={source.data.last_login_at} />],
                      ['Last active', <WhenSeen value={source.data.last_active_at} />],
                      [
                        'Account reference',
                        <CopyableId value={source.data.id} label="member id" full />,
                      ],
                    ]}
                  />
                  <ConsumerOperatorActions member={source.data} onUpdated={source.refresh} />
                  <PasswordResetLink id={id} />
                  <OnboardingNudge id={id} />
                </Section>
                <OnboardingProfile member={source.data} />
                <MemberActions
                  id={id}
                  member={source.data}
                  onUpdated={() => source.refresh({ preserve: true })}
                />
                <MemberDataExport id={id} />
                <MemberAccountDeletion id={id} />
                <MemberNotificationPreferences id={id} />
                <MemberUsageLimits id={id} />
                <MemberGenerationActions id={id} />
              </div>
            ) : null}
          </State>
        }
      />
    </Page>
  );
}

export function ConsumerCreate() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const roster = useLoad<OrForbidden<StylistSummary[]>>(readStylistRoster, []);
  const stylists = roster.data === FORBIDDEN || !roster.data ? [] : roster.data;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (field: string) => {
      const value = data.get(field);
      return typeof value === 'string' ? value.trim() : '';
    };
    setLoading(true);
    setError(undefined);
    configureAdminApi();
    const stylistId = text('assigned_stylist_id');
    const result = await createUserApiV1InternalUsersPost({
      body: {
        name: text('name'),
        email: text('email'),
        status: 'invited',
        // Omitted rather than sent empty: a blank string is not a phone number
        // and the API stores it as one.
        ...(text('phone') ? { phone: text('phone') } : {}),
        ...(text('building') ? { building: text('building') } : {}),
        ...(text('apartment') ? { apartment: text('apartment') } : {}),
        ...(stylistId ? { assigned_stylist_id: stylistId } : {}),
      },
    });
    handleUnauthorized(result.response.status);
    setLoading(false);
    if (result.error || !result.data) return setError('The consumer could not be created.');
    void navigate(`/consumers/${result.data.id}`, { replace: true });
  };
  return (
    <Page
      title="Create consumer"
      purpose="Create a member's account. This only makes the record and emails their invitation — booking their onboarding session is a separate step from the Consumers list afterwards. Everything but the name and email can be left blank and set later on their own page."
    >
      <FormCard title="Consumer details" onSubmit={(event) => void submit(event)}>
        <TextField label="Full name" name="name" required maxLength={200} />
        <TextField label="Email" name="email" type="email" required maxLength={320} />
        <TextField
          label="Phone"
          name="phone"
          type="tel"
          maxLength={40}
          hint="Optional. Stored on the member record and searchable from the Consumers list."
        />
        <TextField
          label="Building"
          name="building"
          maxLength={200}
          hint="Optional. The pilot building this member lives in."
        />
        <TextField
          label="Apartment"
          name="apartment"
          maxLength={100}
          hint="Optional. Their unit within that building."
        />
        <SelectField
          label="Assigned stylist"
          name="assigned_stylist_id"
          disabled={roster.loading}
          hint="Optional, and only active stylists are offered. Assigning here does not book an onboarding session; each stylist shows how many members are already assigned to them."
          options={[
            { value: '', label: 'No stylist yet' },
            ...stylists.map((item) => ({
              value: item.id,
              label: stylistPickerLabel(item),
            })),
          ]}
        />
        {roster.error ? (
          <span className="text-xs text-error-primary">{roster.error}</span>
        ) : roster.data === FORBIDDEN ? (
          <span className="text-xs text-tertiary">
            Your role cannot read the stylist roster, so this member can only be created unassigned.
          </span>
        ) : null}
        {error ? <Callout tone="error" title={error} /> : null}
        <Actions>
          <Button size="md" color="secondary" href="/consumers">
            Cancel
          </Button>
          <Button size="md" color="primary" type="submit" isDisabled={loading}>
            {loading ? 'Creating…' : 'Create consumer'}
          </Button>
        </Actions>
      </FormCard>
    </Page>
  );
}

/**
 * Roster status as the stylists table stores it, plus the `all` sentinel that
 * asks for no filter at all. There is no delete anywhere under the internal
 * API, and there could not be one: a stylist id is referenced by assignments,
 * review batches, tasks and audit rows. `disabled` is the archive, so the
 * roster defaults to `active` rather than showing archived people forever.
 */
const STYLIST_ROSTER_STATUSES = ['all', 'invited', 'active', 'disabled'] as const;
type StylistRosterStatus = (typeof STYLIST_ROSTER_STATUSES)[number];

function resolveRosterStatus(value: string | null): StylistRosterStatus {
  return STYLIST_ROSTER_STATUSES.find((item) => item === value) ?? 'active';
}

export function StylistList() {
  const [params, setParams] = useSearchParams();
  const query = params.get('query') ?? '';
  const status = resolveRosterStatus(params.get('status'));
  const offset = Number(params.get('offset') ?? 0);
  const source = useLoad<StylistPage>(async () => {
    const result = await listStylistsApiV1InternalStylistsGet({
      query: {
        limit: LIMIT,
        offset,
        ...(query ? { query } : {}),
        ...(status === 'all' ? {} : { status }),
      },
    });
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) throw new Error();
    return result.data;
  }, [query, status, offset]);
  const navigate = (next: { query?: string; status?: string; offset?: number }) =>
    setParams({
      ...((next.query ?? query) ? { query: next.query ?? query } : {}),
      // Always written out: the default hides archived stylists, so the view
      // has to be readable from the address bar rather than assumed.
      status: next.status ?? status,
      ...(next.offset ? { offset: String(next.offset) } : {}),
    });
  // Both sums come from the server, over the roster these filters match.
  // Capacity is a number set on each stylist and nothing enforces it, so it is
  // shown on its own rather than as a share of the members assigned.
  const roster = source.data?.breakdown;
  const carried = roster?.assigned_members;
  const capacity = roster?.capacity_per_day;
  return (
    <Page
      title="Stylists"
      purpose="Use this page to see who is on the stylist roster, how many members are assigned to each, and whether they have accepted their invitation yet. It shows active stylists unless you ask for another status: a stylist is never deleted, only disabled, and disabled ones are under Disabled."
      band={
        <MetricBand
          label="The roster"
          figures={[
            matchingFigure(source.data?.total, 'stylist', 'Stylists'),
            {
              id: 'carried',
              label: 'Members assigned',
              ...(carried === undefined
                ? { absent: NOT_YET }
                : {
                    value: formatCount(carried),
                    unit: plural(carried, 'member'),
                    raw: carried,
                  }),
              hint: `Added up across every stylist these filters match, not only the ${LIMIT} a page holds.`,
            },
            {
              id: 'capacity',
              label: 'Daily capacity (not enforced)',
              ...(capacity === undefined
                ? { absent: NOT_YET }
                : {
                    value: formatCount(capacity),
                    unit: plural(capacity, 'member'),
                    raw: capacity,
                  }),
              hint: 'The daily capacities set on every matching stylist, added up. Nothing enforces them: a stylist can be given more members than this.',
            },
          ]}
        />
      }
      actions={
        <Button size="md" color="primary" href="/stylists/new">
          Invite stylist
        </Button>
      }
      toolbar={{
        search: {
          initial: query,
          placeholder: 'Name or work email',
          onSearch: (value) => navigate({ query: value, offset: 0 }),
        },
        onRefresh: () => void source.refresh(),
        filters: (
          <>
            <SelectField
              label="Roster status"
              value={status}
              onChange={(event) => navigate({ status: event.currentTarget.value, offset: 0 })}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'invited', label: 'Invited' },
                { value: 'disabled', label: 'Disabled (archived)' },
                { value: 'all', label: 'All statuses' },
              ]}
            />
          </>
        ),
      }}
    >
      <State {...source} empty={!source.data?.items.length} onRetry={() => void source.refresh()}>
        <RecordTable
          noun="stylists"
          searchable={false}
          records={source.data?.items ?? []}
          columns={['id', 'email', 'status', 'assigned_members', 'capacity_per_day']}
          rowKind="stylist"
          columnLabel={(column) =>
            column === 'assigned_members'
              ? 'Members assigned'
              : column === 'capacity_per_day'
                ? 'Daily capacity (not enforced)'
                : undefined
          }
        />
        {source.data ? (
          <Pager {...source.data} onPage={(next) => navigate({ offset: next })} />
        ) : null}
      </State>
    </Page>
  );
}

const UNACTIONED_MEANING =
  'Nobody reviewed the look before the daily cutoff, so it went to the member exactly as it was generated.';

/**
 * Whether this stylist is keeping up with their members' day-ahead looks: how
 * many are waiting on them, how many they reviewed, and how many went out
 * without anyone looking. The question a stylist page exists to answer.
 */
function StylistOversight({ id }: { id: string }) {
  const [offset, setOffset] = useState(0);
  const source = useLoad<OrForbidden<AdminStylistOversightPage>>(async () => {
    const result = await stylistOversightApiV1InternalStylistsIdOversightGet({
      path: { id },
      query: { limit: LIMIT, offset },
    });
    handleUnauthorized(result.response.status);
    if (result.response.status === 403) return FORBIDDEN;
    if (result.error || !result.data)
      throw new Error('This stylist’s review record could not be loaded.');
    return result.data;
  }, [id, offset]);
  const data = source.data === FORBIDDEN ? undefined : source.data;
  const window = data
    ? `${new Date(`${data.window_start}T00:00:00`).toLocaleDateString()} – ${new Date(`${data.window_end}T00:00:00`).toLocaleDateString()}`
    : '';
  return (
    <Section
      title="Are they keeping up?"
      description="Their members' day-ahead looks: what is waiting on them, what they reviewed, and what went out unreviewed."
      actions={
        <Button size="sm" color="secondary" onClick={() => void source.refresh()}>
          Refresh
        </Button>
      }
    >
      <State {...source} onRetry={() => void source.refresh()}>
        {source.data === FORBIDDEN ? (
          <PermissionBoundary subject="this stylist’s review record" />
        ) : data ? (
          <div className="flex flex-col gap-4">
            {data.shipped_unactioned > 0 ? (
              <Callout
                tone="error"
                title={`${formatCount(data.shipped_unactioned)} of their members' looks went out unreviewed in the last ${formatCount(data.window_days)} days.`}
              >
                <Term term="Unreviewed" definition={UNACTIONED_MEANING} /> looks reached the member
                without a stylist checking them. This is the number that says someone is behind.
              </Callout>
            ) : null}
            <StatRow>
              <StatTile
                label="Assigned members"
                value={
                  <Quantity
                    value={formatCount(data.assigned_members)}
                    unit="members"
                    raw={data.assigned_members}
                  />
                }
                hint="Members whose looks this stylist is responsible for."
              />
              <StatTile
                label="Waiting for review"
                value={
                  <Quantity
                    value={formatCount(data.awaiting_review)}
                    unit="looks"
                    raw={data.awaiting_review}
                  />
                }
                tone={data.awaiting_review > 0 ? 'warning' : 'success'}
                statusLabel={data.awaiting_review > 0 ? 'to do' : 'clear'}
                hint="Looks for the members assigned to them now, waiting in the queue, any date."
              />
              <StatTile
                label="Reviewed"
                value={
                  <Quantity value={formatCount(data.approved)} unit="looks" raw={data.approved} />
                }
                hint={`Looks this stylist approved for any member, including members no longer assigned to them. ${formatCount(data.approved_as_is)} approved as generated, ${formatCount(data.edited)} changed · ${window}.`}
              />
              <StatTile
                label={
                  <Term term="unreviewed" definition={UNACTIONED_MEANING}>
                    Went out unreviewed
                  </Term>
                }
                value={
                  <Quantity
                    value={formatCount(data.shipped_unactioned)}
                    unit="looks"
                    raw={data.shipped_unactioned}
                  />
                }
                tone={data.shipped_unactioned > 0 ? 'error' : 'success'}
                statusLabel={data.shipped_unactioned > 0 ? 'behind' : 'on time'}
                hint={`Looks for the members assigned to them now · ${window}.`}
              />
            </StatRow>
            {data.assigned_members === 0 ? (
              <Callout tone="neutral" title="No members are assigned to this stylist.">
                Assign members from the stylist assignments page; their day-ahead looks will then
                show up here.
              </Callout>
            ) : null}
            <p className="text-sm text-tertiary">
              Every day-ahead look for the members assigned to them now, all dates, newest first.
            </p>
            <RecordTable
              noun="day-ahead looks"
              searchable={false}
              records={data.items}
              columns={[
                'for_date',
                'member_id',
                'review_status',
                'decision',
                'reviewed_at',
                'reviewed_by',
                'stylist_comment',
              ]}
              emptyHint="No day-ahead looks for this stylist's members on this page."
            />
            <Pager offset={data.offset} limit={data.limit} total={data.total} onPage={setOffset} />
          </div>
        ) : null}
      </State>
    </Section>
  );
}

export function StylistDetails() {
  const { id = '' } = useParams();
  const source = useLoad<StylistDetail>(async () => {
    const result = await getStylistApiV1InternalStylistsIdGet({ path: { id } });
    handleUnauthorized(result.response.status);
    if (result.response.status === 405) {
      for (let offset = 0; offset < 2_000; offset += 100) {
        const fallback = await listStylistsApiV1InternalStylistsGet({
          query: { limit: 100, offset },
        });
        handleUnauthorized(fallback.response.status);
        if (fallback.error || !fallback.data) break;
        const match = fallback.data.items.find((item) => item.id === id);
        if (match) return match;
        if (offset + fallback.data.items.length >= fallback.data.total) break;
      }
      throw new Error(
        'Stylist detail is awaiting the Admin backend deployment. Return to Stylists or refresh after deployment.',
      );
    }
    if (result.error || !result.data) throw new Error('Stylist details could not be loaded.');
    return result.data;
  }, [id]);
  return (
    <Page
      title={source.data?.name ?? 'Stylist details'}
      purpose="This stylist's roster position, the members assigned to them, whether they are keeping up with what they were given to review, and the audited controls that change any of it."
      actions={
        <Button size="md" color="secondary" href="/stylists">
          Back to stylists
        </Button>
      }
    >
      <State {...source} onRetry={() => void source.refresh()}>
        {source.data ? (
          <Section title="Stylist record">
            <DetailList
              items={[
                ['Work email', source.data.email],
                ['Status', <Status value={source.data.status} />],
                [
                  'Members assigned',
                  <Quantity
                    value={formatCount(source.data.assigned_members)}
                    unit={plural(source.data.assigned_members, 'member')}
                    raw={source.data.assigned_members}
                  />,
                ],
                [
                  'Daily capacity',
                  <span>
                    <Quantity
                      value={formatCount(source.data.capacity_per_day)}
                      unit="members per day"
                      raw={source.data.capacity_per_day}
                    />{' '}
                    (not enforced)
                  </span>,
                ],
              ]}
            />
            <StylistOperatorActions stylist={source.data} onUpdated={source.refresh} />
          </Section>
        ) : null}
      </State>
      {/* Staff need recovery links for the same reason members do, and more
          often: the route already gates a staff target behind super-admin
          rather than refusing it, so the only thing missing here was the
          control. */}
      {source.data ? (
        <StylistMembers
          stylistId={id}
          stylistName={source.data.name}
          onChanged={() => source.refresh({ preserve: true })}
        />
      ) : null}
      <PasswordResetLink id={id} />
      <StylistOversight id={id} />
    </Page>
  );
}

export function StylistCreate() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = data.get('name');
    const email = data.get('email');
    const capacity = data.get('capacity');
    setLoading(true);
    setError(undefined);
    configureAdminApi();
    const result = await createStylistApiV1InternalStylistsPost({
      body: {
        name: typeof name === 'string' ? name : '',
        email: typeof email === 'string' ? email : '',
        capacity_per_day: typeof capacity === 'string' ? Number(capacity) : 0,
        status: 'invited',
      },
    });
    handleUnauthorized(result.response.status);
    setLoading(false);
    if (result.error || !result.data)
      return setError(errorDetail(result.error, 'The invitation could not be created.'));
    void navigate(`/stylists/${result.data.id}`, { replace: true });
  };
  return (
    <Page
      title="Invite stylist"
      purpose="Create a stylist's account and email them a link to set their own password. They can only reach the stylist tools, never this console."
    >
      <FormCard title="Stylist details" onSubmit={(event) => void submit(event)}>
        <TextField label="Full name" name="name" required maxLength={200} />
        <TextField label="Work email" name="email" type="email" required maxLength={320} />
        <TextField
          label="Daily member capacity"
          name="capacity"
          hint="How many members they can be assigned in one day, from 1 to 100. You can change this later on their own page."
          type="number"
          min={1}
          max={100}
          defaultValue={8}
          required
        />
        {error ? <Callout tone="error" title={error} /> : null}
        <Actions>
          <Button size="md" color="secondary" href="/stylists">
            Cancel
          </Button>
          <Button size="md" color="primary" type="submit" isDisabled={loading}>
            {loading ? 'Sending…' : 'Send invitation'}
          </Button>
        </Actions>
      </FormCard>
    </Page>
  );
}
